/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable no-prototype-builtins */

// @ts-strict-ignore
import { BehaviorSubject, Subject, Subscription, filter } from "rxjs";
import { Company, CompanyInfo } from 'rainbow-web-sdk/models/company.model';
import { PersonalRoutine } from 'rainbow-web-sdk/models/personalRoutine.model';
import { TelephonyForward } from 'rainbow-web-sdk/models/telephonyForward.model';
import { NetworkInvitation } from 'rainbow-web-sdk/models/invitation.model';
import { RBEvent } from "./event.model";
import { MultiFactorAuthRainbowAuth, MultiFactorAuthStatus, MultiFactorAuthType } from "./rainbow-auth.model";
import { ConnectedUserOxeTrunk, DisplayOrder, PostalAddress, User, UserAvatar, UserCustomStatus, UserEmailAddress, UserEvents, UserNotes, UserOwnFeatureCapabilities, UserPeerFeatureCapabilities, UserPhoneNumber } from 'rainbow-web-sdk/models/user.model';
import { Conversation } from "./conversation.model";
import { ContactService } from "../services/core/contact.service";
import { PresenceHelperService } from "../services/core/presenceHelper.service";

/**
* List of different types of users.
*/
export enum UserType {
    /** Rainbow User */
    USER = 'User',

    /** Microsoft Azure Directory contact */
    O365_CONTACT = 'O365Contact',

    /** Outlook contact (Desktop only) */
    OUTLOOK_CONTACT = 'OutlookContact',

    /**  Business Directory contact */
    BUSINESS_CONTACT = 'BusinessContact',

    /** Personal Directory contact */
    PERSONAL_CONTACT = 'PersonalContact',

    /** PBX phonebook contact */
    PHONEBOOK_CONTACT = 'PhonebookContact',

    /** BOT */
    BOT = 'Bot',

    /** MCU BOT */
    MCUBOT = 'McuBot',

    /** Rainbow Room */
    RAINBOW_ROOM = 'RainbowRoom',

    /** Temporary contact used for internal purpose */
    TEMP = 'TemporaryInternal',
}

/**
* List of different email types a User can have.
*/
export enum EmailType {
    /** Business email */
    BUSINESS = 'Business',

    /** Personal email */
    PERSONAL = 'Personal'
}

/**
 * List of different phone number types a User can have.
 * Several telephone numbers can be defined for a user. This number depends on the type of user and his configuration:
 * - The phone numbers the user specified in his profile or contact card.
 * - The phone numbers defined by the administrator of his company.
 *   This applies in particular to the business number and the internal number associated with the telephony service.
 *   These numbers cannot be changed by an end-user.
 * For a user, it is only possible to define one type of phone number.
 */
export enum PhoneNumberType {
    /** Business landline phone number */
    BUSINESS = 'Business',

    /** Business mobile phone number */
    BUSINESS_MOBILE = 'BusinessMobile',

    /** Business internal phone number */
    BUSINESS_INTERNAL = 'BusinessInternal',

    /** Personal phone number */
    PERSONAL = 'Personal',

    /** Personal phone number */
    PERSONAL_MOBILE = 'PersonalMobile',

    /** Other type of phone number */
    OTHER = 'Other'
}

/*
 * This interface is used to represent the List of permission types for receiving files in a P2P conversation or in a bubble.
 * Depending on the value, the contact may have some restriction regarding receiving files.
 */
export enum ReceivedFileCustomisationValue {
    ENABLED = "enabled",
    DISABLED = "disabled",
    INTERNAL_ONLY = "internalOnly"
}

/**
 * @internal
 */
export enum ccdModes {
    DEFAULT = "default",
    MIXED = "mixed",
    FIXED = "fixed"
}

/**
 * This interface is used to define the list of admin subcategories for user with the 'admin' role.
 */
export enum AdminType {
    ORGANIZATION_ADMIN = "organization_admin",
    COMPANY_ADMIN = "company_admin",
    SITE_ADMIN = "site_admin",
    COMPANY_SUPPORT = "company_support",
    UNDEFINED = "undefined"
};

/**
 * @internal
 * State of the Office365 subscription of Rainbow user.
 * Available only for the connected user and when Office365 is configured on admin side.
 */
export enum O365SubscriptionState {
    /** No Calendar events / Teams presence synchronization activated */
    INACTIVE = 'inactive',

    /**  */
    PENDING = 'pending',

    /** Synchronization of Calendar events + Teams presence activated*/
    ACTIVE = 'active',

    /** Only Calendar events synchronization is activated   */
    ACTIVE_CALENDAR = 'active_calendar',

    /**  Only Teams presence synchronization is activated */
    ACTIVE_TEAMS = 'active_teams',

    /**  */
    ERROR = 'error'
}

/**
 * This interface is used to define the list of roles a user can have.
 */
export enum UserRole {
    USER = "user",
    GUEST = "guest",
    SUPERADMIN = "superadmin",
    SUPPORT = "support",
    BUSINESS_ADMIN = "business_admin",
    CUSTOMER_SUCCESS_ADMIN = "customer_success_admin",
    ADMIN = "admin",
    BP_ADMIN_OPERATIONS = "bp_admin",
    BP_ADMIN_FINANCE = "bp_finance",
    BP_ADMIN_ANALYTICS = "bp_analytics",
    EC_ADMIN_ANALYTICS = "ec_analytics",
    DIRECTORY_ADMIN = "directory_admin",

    /**
     * Right to create a channel with a visibility for all users (users of my company and other companies).
     */
    PUBLIC_CHANNELS_ADMIN = "public_channels_admin",

    /**
     * Right to create a channel with a visibility limited to my company and a "closed" confidentiality.
     * Selected or all users of my company will automatically be subscribed to the channel. They will not be able to unsubscribe.
     */
    CLOSED_CHANNELS_ADMIN = "closed_channels_admin",

    /**
     * Right to create a channel with a visibility limited to my company and a "closed" confidentiality
     * All users of my company will automatically be subscribed to the channel. They will not be able to unsubscribe.
     */
    ALL_COMPANY_CHANNELS_ADMIN = "all_company_channels_admin",

    SALES_ANALYTICS = "sales_analytics",

    /** User allowed to get privacy data for any user within his company */
    PRIVACY_OPERATIONS = "privacy_operations",

    /** User allowed to create/update/launch webinars */
    WEBINAR_HOST = "webinar_host",

    /** User is supervisor of a supervision group. */
    GROUP_SUPERVISOR = "supervisor",

    /** Role allowing multiline softphone for Attendant Console features */
    ATTENDANT_CONSOLE = "attendant" // attendant console role
};

/**
 * Interface used only by UCaaS (Web Client) to manage the user connection wizard (1st connection).
 * This interface must not be exposed to SDKv2, as properties manipulate data with names and values that are strongly correlated with the server
 * Note that this data may be exposed to SDKv2 under another name and/or format, or not at all.
 * @internal
 */
export interface ConnectedUserInit {
    /**
     * This data must be set to 'true' as soon as the user is considered initialized, i.e.
     * when he enters his firstname and lastname on his first connection.
     */
    isInitialized?: boolean;

    /**
     * User visibility
     * Specify whether the user can be searched by users in other companies and whether the user can search for users in other companies.
     */
    visibility?: string;

    /**
     * Defines the user as a guest
     * This allows someone who doesn't have a Rainbow user account to join a bubble and participate in the conversation thanks to an e-mail invitation.
     */
    guestMode?: boolean;

    /**
     * The guest user's company name
     */
    companyNameOfGuest?: string;
}

export interface CcdAgent {
    mode: ccdModes;
    agentPhoneNumber: string;
    isCCDAgentLogged?: boolean;
}

/**
 * Interface used only by UCaaS (Web Client) to manage user communication information such as emails and phone numbers.
 * This interface must not be exposed to SDKv2, as the properties (typed 'any') manipulate data with names and values
 * that are strongly correlated with the server.
 * Note that this data may be exposed to SDKv2 under another name and/or format, or not at all.
 * @internal
 */
export interface ConnectedUserMedia {
    /**
     * Array of user emails addresses, each address entry represented by an JSON object in the format
     *      \{email: ..., type: ... \}
     */
    emails?: any[];

    /**
     * Array of user phone numbers, each number entry represented as an JSON object
     *      \{ number: ..., type:...,, deviceType: ...,, isVisibleByOthers: ... \}
     */
    phoneNumbers?: any[];
}

/**
 * Interface used (only for hub case) to store all secondary Ddi phone numbers.
 * @internal
 */
export interface SecondaryDdiNumber { //RQRAINB-11807
    phonePro: string;
    phoneProCan: string;
    secondaryDdiOrder: number;
}

/**
 * This class is used to represent a contact or the connected user.
 * A contact is defined by a set of public information (name, firstname, avatar...) and a set of private information that are only shared with contacts that are in the user's network or in the same company (email, phone numbers...)
 * @internal
 */
export class Contact implements User {

    // ====================================================
    //               ENUM & TYPE
    // ====================================================

    public static SIPWISE_RVCP: string = "RVCP Subscriber";

    public static NameUpdatePrio = {
        NO_UPDATE_PRIO: 0,
        OUTLOOK_UPDATE_PRIO: 1,
        SERVER_UPDATE_PRIO: 2,
        MAX_UPDATE_PRIO: 99
    };

    public static textAvatarColor = [
        "#c22828",
        "#ce471d",
        "#c62582",
        "#9b4cE7",
        "#a15e9d",
        "#7065bd",
        "#5565f1",
        "#1b70e0",
        "#3778b9",
        "#547d66",
        "#218500",
        "#1c6f3c"
    ];

    public static textAvatarName = [
        "purple-lavender",
        "purple-violet",
        "red-coral",
        "blue-jeanlouisdavid",
        "green-mint",
        "green-yellow",
        "orange-jade",
        "pink-lady",
        "yellow-submarine",
        "blue-teal"
    ];

    // ====================================================
    //                       DATA
    // ====================================================

    // --------------------------------------------------------------------------------------------
    // UCaaS + SDKv2 public Properties
    //       + SDKv2 Getter/Setter for private properties (if managed so !)
    // --------------------------------------------------------------------------------------------

    /** Weak reference on contactService */
    protected contactService?: ContactService = ContactService.getInstance();
    protected presenceHelperService?: PresenceHelperService = PresenceHelperService.getInstance();

    // User INTERNAL DATA STUFF
    // --------------------------------
    /** The contact IDentifier */
    public id: string = null;

    /** The contact internal id */
    public dbId: string = null;

    /** The contact XMPP bare jid */
    public jid: string = null;

    /** The contact XMPP full jid */
    public fullJid: string = null;

    /** The contact telephony XMPP jid */
    public jidtel: string = null;

    // User LOGIN STUFF
    // --------------------------------
    /** The User main email adress which can also be used for login */
    public loginEmail: string = null;

    /** Authentication/login via phone number */
    // NOTE: SDKv2 availability requires adaptation in connection.service
    public loginPhone: string = null;

    // User IDENTITY STUFF
    // --------------------------------
    /** The contact title */
    public title: string = null;

    /** The contact lastname */
    public lastname: string = null;

    /** The contact firstname */
    public firstname: string = null;

    /** The contact nickname */
    public nickname: string = null;

    /** The contact initials */
    public initials: string = null;

    /** Display format for users' full names (first and last names) data. */
    public displayOrder: DisplayOrder = "FL";

    /** The job title of the contact */
    public jobTitle: string = null;

    /** The contact language */
    public language: string = null;

    /**The Country of the contact */
    public country: string = null;

    /** The timezone of the contact*/
    public timezone: string = null;

    // Avatar
    /** The user avatar object */
    public set avatar(avatar: UserAvatar) {
        this._avatar = avatar;
    }

    public get avatar(): UserAvatar {
        if (this._avatar) return this._avatar;
        if (this.avatarSrc) {
            this._avatar = { src: this.avatarSrc };
            this.defaultAvatar = null;
            this.rxSubject.next({ name: "avatar" });
            return this._avatar;
        }
        if (!this.defaultAvatar) this.createTextAvatarImage();
        return this.defaultAvatar;
    }

    /** The user avatar last modification date */
    public lastAvatarUpdateDate: any;

    // User STATUS STUFF
    // --------------------------------
    /** Presence status of the user*/
    public status: 'online' | 'away' | 'offline' | 'unknown' | string = null;
    public get statusInfo(): string { return this.message; }
    public set statusInfo(info: string) { this.message = info; }

    // User MANAGEMENT STUFF
    // --------------------------------
    /** Indicates whether the contact is fully initialized */
    public initialized: boolean = false;

    /** Indicates whether the user has been deleted */
    public isTerminated: boolean = false;
    public get terminated(): boolean { return this.isTerminated; }

    // User PHONE NUMBERS STUFF
    // --------------------------------
    public get phoneNumbers(): UserPhoneNumber[] {
        const myPhoneNumbers: UserPhoneNumber[] = [];
        const isConnectedUser: boolean = this.isConnectedUser();

        // BUSINESS LANDLINE phone number
        // if (this.phonePbx && !this.phonePro) {
        //     myPhoneNumbers.push({type: PhoneNumberType.BUSINESS, number: this.phonePbx, shared: true, readOnly: false });
        // }

        if (this.phonePro && this.phoneProCan) {
            // If a PBX is detected, this business number can't be updated
            const updateNotAllowed: boolean = (this.pbxId.length > 1);
            myPhoneNumbers.push({ type: PhoneNumberType.BUSINESS, number: this.phoneProCan, shared: true, readOnly: (updateNotAllowed || !isConnectedUser) });
        }

        // Business number from Directories (Business, Personal, O365, Outlook)
        if (this.businessPhones?.length) {
            this.businessPhones.forEach((businessPhone) => {
                if (businessPhone?.length > 0) { myPhoneNumbers.push({ type: PhoneNumberType.BUSINESS, number: businessPhone, shared: true, readOnly: true }); }
            });
        }

        // BUSINESS MOBILE phone number
        if (this.mobilePro && this.mobileProCan) {
            myPhoneNumbers.push({ type: PhoneNumberType.BUSINESS_MOBILE, number: this.mobileProCan, shared: this.isMobileProShared, readOnly: !isConnectedUser });
        }

        // Business mobile phone number from Directories (Business, Personal, O365, Outlook)
        if (this.mobilePhones?.length) {
            this.mobilePhones.forEach((mobilePhone) => {
                if (mobilePhone?.length > 0) { myPhoneNumbers.push({ type: PhoneNumberType.BUSINESS_MOBILE, number: mobilePhone, shared: true, readOnly: true }); }
            });
        }

        // BUSINESS INTERNAL phone number
        if (this.phoneInternalNumber) {
            myPhoneNumbers.push({ type: PhoneNumberType.BUSINESS_INTERNAL, number: this.phoneInternalNumber, shared: true, readOnly: true });
        }

        // PERSONAL LANDLINE phone number
        if (this.phonePerso && this.phonePersoCan) {
            myPhoneNumbers.push({ type: PhoneNumberType.PERSONAL, number: this.phonePersoCan, shared: this.isPhonePersoShared, readOnly: !isConnectedUser });
        }

        // PERSONAL MOBILE phone number
        if (this.mobilePerso && this.mobilePersoCan) {
            myPhoneNumbers.push({ type: PhoneNumberType.PERSONAL_MOBILE, number: this.mobilePersoCan, shared: this.isMobilePersoShared, readOnly: !isConnectedUser });
        }

        // OTHER numbers (Personal + Business Directory contact only)
        if (this.otherPhoneNumbers?.length && this.otherPhoneNumbers?.forEach) {
            this.otherPhoneNumbers.forEach((otherPhone) => {
                if (otherPhone?.length > 0) { myPhoneNumbers.push({ type: PhoneNumberType.OTHER, number: otherPhone, shared: true, readOnly: true }); }
            });
        }

        // ALIAS internal numbers (Business Directory contact only)
        if (this.aliasesPhones?.length) {
            myPhoneNumbers.push({ type: PhoneNumberType.BUSINESS_INTERNAL, number: this.aliasesPhones[0].code, shared: true, readOnly: true });
        }

        return myPhoneNumbers;
    }

    // User EMAIL ADDRESS STUFF
    // --------------------------------
    public get emails(): UserEmailAddress[] {
        const userEmails: UserEmailAddress[] = [];

        // Personal Email
        if (this.emailPerso) {
            userEmails.push({ type: EmailType.PERSONAL, address: this.emailPerso });
        }

        // Business Email
        if (this.emailPro) {
            userEmails.push({ type: EmailType.BUSINESS, address: this.emailPro });
        }

        if (this.email) {
            if (this.personalDirectoryFlag) {
                userEmails.push({ type: EmailType.PERSONAL, address: this.email });
            } else if (this.companyDirectoryFlag || this.o365Flag || this.outlookFlag) {
                userEmails.push({ type: EmailType.BUSINESS, address: this.email });
            }
        }
        return userEmails;
    }

    // User NOTES STUFF
    // --------------------------------
    public get notes(): UserNotes {
        const userNotes: UserNotes = {
            note1: "",
            note2: ""
        };

        if (this.note1) {
            userNotes.note1 = this.note1;
        }

        if (this.note2) {
            userNotes.note2 = this.note2;
        }

        return userNotes;
    }

    // User POSTAL ADRRESS STUFF
    // --------------------------------
    /** Data aggregating all properties related to a postal address. */
    public get postalAddress(): PostalAddress {
        const userPostalAddress: PostalAddress = {
            street: "",
            department: "",
            postalCode: "",
            city: "",
            state: ""
        };

        if (this.street) {
            userPostalAddress.street = this.street;
        }

        if (this.department) {
            userPostalAddress.department = this.department;
        }

        if (this.postalCode) {
            userPostalAddress.postalCode = this.postalCode;
        }

        if (this.city) {
            userPostalAddress.city = this.city;
        }

        if (this.state) {
            userPostalAddress.state = this.state;
        }

        return userPostalAddress;
    }

    // User TYPE STUFF
    // --------------------------------
    /** Data aggregating flags already defined to characterise a contact. */
    public get userType(): UserType {

        if (this.o365Flag)
            return UserType.O365_CONTACT;

        if (this.outlookFlag)
            return UserType.OUTLOOK_CONTACT;

        if (this.companyDirectoryFlag)
            return UserType.BUSINESS_CONTACT;

        if (this.personalDirectoryFlag)
            return UserType.PERSONAL_CONTACT;

        if (this.phonebookDirectoryFlag)
            return UserType.PHONEBOOK_CONTACT;

        if (this.isBot)
            return UserType.BOT;

        if (this.isTv)
            return UserType.RAINBOW_ROOM;

        if (this.temp)
            return UserType.TEMP;

        if (this.jid.startsWith("mcu_"))
            return UserType.MCUBOT;

        return UserType.USER;
    }

    // User GUEST STUFF
    // --------------------------------
    public get guest(): boolean {
        return this._guestMode;
    }

    public companyNameOfGuest: string;

    // USER CAPABILITES STUFF
    // --------------------------------
    /** Data aggregating all the User feature capabilities */
    public get ownFeatureCapabilities(): UserOwnFeatureCapabilities {
        return {
            presenceUpdateEnabled: this.changePresenceEnabled,
            presenceSetInvisibleEnabled: this.canSetInvisiblePresenceCustomisation,
            IMEnabled: this.instantMessagesEnabled,
            IMReadReceiptsEnabled: this.readReceiptsCustomisation,
            documentStorageEnabled: this.fileStorageEnabled,
            documentSharingEnabled: this.fileSharingEnabled,
            documentTransferEnabled: this.fileTransferEnabled,
            channelEnabled: this.channelsEnabled,
            bubbleEnabled: this.roomsEnabled,
            bubbleUseSpeakingTimeStatsEnabled: this.useSpeakingTimeStatistics,
            bubbleConferenceRecordingEnabled: this.conferenceRecordingEnabled,
            callWebrtcAudioEnabled: this.webrtcAudioEnabled,
            callWebrtcVideoEnabled: this.webrtcVideoEnabled,
            callWebrtcSharingEnabled: this.webrtcSharingEnabled,
            callConversationRecordingEnabled: this.recordConversationEnabled,
            profileUpdateEnabled: this.modifyProfileEnabled,
            profileTitleNameUpdateEnabled: this.userTitleNameEnabled,
            profileAvatarCustomizationEnabled: this.userAvatarCustomizationEnabled,
            settingsUpdateEnabled: this.changeSettingsEnabled,
            taskEnabled: this.canUseTaskCustomisation,
            calendarSynchEnabled: this.canSynchCalendar,
            transcriptionEnabled: this.canUseTranscriptionCustomisation
        };
    }

    /** Data aggregating all the User feature capabilities */
    public get peerFeatureCapabilities(): UserPeerFeatureCapabilities {
        return {
            IMReceiveAlertNotificationEnabled: this.isAlertNotificationEnabled,
            documentReceivedFilePermission: this.receivedFileCustomisation
        };
    };

    // ADMIN STUFF
    // --------------------------------
    /** The different roles assigned to the connected user  */
    public roles: UserRole[] = null;

    /** If the connected user has the ‘admin’ role, specify the sub-category of this role. */
    public adminType: AdminType = null;

    /** Indicates whether the user's company has the ALERT_NOTIFICATIONS_ALLOWED feature available via the ALERT offer. */
    public alertNotificationEnabledForMyCompany: boolean = false;

    // PBX TELEPHONY STUFF (Voicemail...)
    // ---------------------------------------------------------
    public voicemailNumber: string = null;

    private _customStatus: UserCustomStatus = null;
    public get customStatus(): UserCustomStatus {
        return this._customStatus;
    }

    public set customStatus(value: UserCustomStatus) {
        this._customStatus = value;
        this.rxSubject.next({ name: "customStatus" });
    }

    // USER ECOSYSTEM STUFF (Company...)
    // ---------------------------------------------------------
    /** Getter for building some company information based on contact type */
    public get companyInfo(): CompanyInfo {
        const compInfo: CompanyInfo = { name: "" };

        if (this.isUserTypeOf(UserType.USER) || this.isUserTypeOf(UserType.BOT)) {
            if (this.company) {
                compInfo.id = this.company.id;
                compInfo.name = this.company.name;
                compInfo.slogan = this.company.slogan;
                compInfo.logo = this.company.logo?.src;
                compInfo.lastAvatarUpdateDate = this.company.lastAvatarUpdateDate;
                compInfo.companyFeatures = this.companyFeatures;
            }
        }

        if (this.isDirectoryContact()) {
            compInfo.name = this.company.name;
        }

        return compInfo;
    }

    /** Indicates whether or not the contact belongs to the default company */
    public isInDefaultCompany: boolean = false;

    // --------------------------------------------------------------------------------------------
    //                  UCaaS Only: Internal Management - NOT used By SDKv2
    //                          (Include getter/setter if managed so !)
    // --------------------------------------------------------------------------------------------

    // User LIFE CYCLE STUFF
    // --------------------------------
    public rxSubject = new Subject<any>();

    /** The subscription state for this contact (none or both) */
    public subscription: string = null;

    /** The subscription progress. Only applies to USER contacts */
    public ask: string = null;

    /** Indicates whether or not the contact is part of the favorized contact's list of the connected user */
    public roster: boolean = false;

    /**
     * Information about the invitation sent by a contact to the connected user.
     * (So not relevant for the connected user itself)
     * Data managed by the userNetwork service.
     */
    public invitation: NetworkInvitation = null;

    /** The associated conference identifier */
    public confId: string = null;

    /** The conversation if exists with this contact. Only applies to USER contacts. */
    public conversation: Conversation = null;

    /**
     * The communication capabilities or the contact, ie the list of communication actions that are currently supported by the contact.
     * This only applies to contacts of the USER type.
     * These capabilities are stored as a JSON object; each key is associated to a flag capability and its value indicates
     * whether it is possible (If 'true') or not to try to add this media.
     * - telephony
     * - webRTC
     * - sharedDesktop
     * - mediaAvailable (means telephony || webRTC)
     * - addMedia (true if user is already in call and has an active fullJid)
     */
    public capabilities: any = null;

    public isConfiguringSettings: boolean = false;

    // User IDENTITY STUFF
    // --------------------------------
    /*
     * The short display name of the contact.
     * Based on other name information such as surname, first name, nickname...
     */
    public displayShortName: string = null;

    /** The display name of the contact, based on the '_displayName' property */
    public name: any = null;

    private _displayName: string = null;
    public get displayName(): string {
        return this._displayName;
    }

    public set displayName(displayName: string) {
        if (this._displayName !== displayName) {
            this._displayName = displayName;
            this.nameForLogs = config?.debug ? displayName : displayName.charAt(0) + displayName.replace(/[^\s](?=.{1,}$)/g, "*").substr(1);
            this.name.value = displayName;
            this.rxSubject.next({ name: "displayName" });
        }
    }

    /**
     * Indicates which service has priority for updating the contact name, based on the name resolution performed by this service.
     * Possible service options:
     * - NO_UPDATE_PRIO: No update priority then could be updated
     * - OUTLOOK_UPDATE_PRIO: Priority associated to outlook name resolution update
     * - SERVER_UPDATE_PRIO: Priority associated to server name resolution (phonebook or office365AD ...) update
     * - MAX_UPDATE_PRIO: No update could overwrite
     */
    public nameUpdatePrio: any = null;

    /** Obscured name of user for logs */
    public nameForLogs: string = null;

    /** Array of the user tags*/
    public tags: string[] = null;

    // User CUSTOM & GENERAL data
    // --------------------------------
    /** User-specific custom data in the form of an Object with a free keys/values structure. */
    public customData = null;

    /** General information attached to the user, can be modified by admin. */
    public userInfo1 = null;

    /** General information attached to the user, can be modified by admin. */
    public userInfo2 = null;

    // User AVATAR STUFF
    // --------------------------------
    /** internal avatar storage variable */
    private _avatar: UserAvatar = null;

    /** The avatar if any */
    public defaultAvatar: any = null;

    /** The avatar URL if any, null if none */
    public avatarSrc: string = null;

    public color: string;
    public colorIndex: number;
    public colorName: string;
    public avatarServerURL: string;

    // User STATUS STUFF
    // --------------------------------
    /** The contact status message */
    public message: string = null;

    /** The telephony status of the contact.*/
    public telStatus: string = null;

    /** The IM status of the contact.*/
    public imStatus: string = null;

    /** The old status before making a call/*/
    public oldImStatus: string = null;

    /** The last IM status for each resource.*/
    public imStatusStamp: any[] = null;

    /** An object containing the list of all resources used by the connected user.*/
    public resources: any = null;

    /** The first mobile resource detected.*/
    public mobileResource: string;

    /** The contact last activity date.*/
    public lastActivityDate: BehaviorSubject<Date | null>;

    // User GUEST STUFF
    // --------------------------------
    private _guestMode: boolean = false;
    public guestModeSubject = new BehaviorSubject<boolean>(this._guestMode);
    public get guestMode(): boolean {
        return this._guestMode;
    }

    public set guestMode(value: boolean) {
        this._guestMode = value;
        this.guestModeSubject.next(this._guestMode);
    }

    /** The right for a guest to join the company by completing the creation of his user account. */
    public denySelfRegister: boolean = false;

    // User Emails STUFF
    // --------------------------------
    /** The professional email of a USER contact */
    public emailPro: string = null;

    /** The personal email of a USER contact */
    public emailPerso: string = null;

    /** The email of a directory contact (from Business, Personal, O365 directories */
    public email: string = "";

    // User PHONE NUMBERS STUFF
    // --------------------------------
    /** The professional phone number of the contact (landline, work) */
    public phonePro: string;

    /** The professional phone number of the contact (canonical format) */
    public phoneProCan: string = null;

    /** A table of secondary Ddi (for hub case only): */ //RQRAINB-11807
    public secondaryDdiNumbers: SecondaryDdiNumber [];

    /** The professional mobile phone of the contact (mobile, work) */
    public mobilePro: string = null;

    /** The professional mobile phone of the contact (canonical format) */
    public mobileProCan: string = null;

    /** Indicates if the professional mobile phone of the contact is visible by other users */
    public isMobileProShared: boolean = false;

    /** The personal phone of the contact (landline, home) */
    public phonePerso: string = null;

    /** The personal phone of the contact (canonical format)*/
    public phonePersoCan: string = null;

    /** Indicates if the personal landline phone of the contact is visible by other users */
    public isPhonePersoShared: boolean = false;

    /** The personal mobile phone of the contact (mobile, home) */
    public mobilePerso: string = null;

    /** The personal mobile phone of the contact (canonical format) */
    public mobilePersoCan: string = null;

    /** Indicates if the personal mobile phone of the contact is visible by other users */
    public isMobilePersoShared: boolean = false;

    /** The phone number if exists associated to the user and monitored by Rainbow */
    public phonePbx: string = null;

    /** Business number from Directories (Business, Personal, O365, Outlook) */
    public businessPhones: string[] = [];

    /** Business mobile phone number from Directories (Business, Personal, O365, Outlook) */
    public mobilePhones: string[] = [];

    /** OTHER numbers (Personal + Business Directory contact only) */
    public otherPhoneNumbers: string[] = null;

    /** ALIAS internal numbers (Business Directory contact only) */
    public aliasesPhones: any[] = []; //RQRAINB-8992

    /**  The internal number if exists associated to the user and monitored by Rainbow */
    public phoneInternalNumber: string = null;

    /** Flag indicating whether at least one telephone number has been defined for the user   */
    // TODO: Use case is UI only (myProfile component) .. It must be reworked to remove the data.
    public hasPhoneNumber: boolean = false;

    /**  Great internal mystery to determine whether the user has a PBX DDI or not */
    public hasPhoneProDdiNumber: boolean = false;

    /** Flag indicating whether all the phone numbers associated with a contact have been retrieve from the server */
    public phoneNumbersInitialized: boolean = false;

    /** Hybrid Telephony, On busy routine: User redirect phone number (as entered by user) */
    public redirectNumber: string = "";

    /** Hybrid Telephony, On busy routine: Indicates if the redirect phone number is editable by a user */
    public isRedirectNumberEditable: boolean = true;

    // User POSTAL ADDRESS STUFF
    // --------------------------------
    /** The department of the contact (Directory contact only) */
    public department: string = null;

    /** The postal code of the contact (Directory contact only) */
    public postalCode: string = null;

    /** The state of the business contact (Directory contact only) */
    public state: string = null;

    /** The street of the business contact (Directory contact only) */
    public street: string = null;

    /** The city of the business contact */
    public city: string = null;

    // User NOTES STUFF
    // --------------------------------
    /** The note1 of a personal or business directory contact */
    public note1: string = null;

    /** The note2 of a personal or business directory contact */
    public note2: string = null;

    // User CALENDAR STUFF
    // --------------------------------
    /**
     * EXPERIMENTAL (this property will likely change in the future version)
     * Object containing all the calendar informations related to user
     * The object contains the current meeting / incoming calendar meetings, but also it contains the Out Of Office informations:
     *      The structure of the object (by default it's empty if no calendar presence) :
     *      calendarInformation = \{
     *          status: string          //calendar status (one of "online", "dnd")
     *          mdate: date             //the date of the end / next meeting
     *          autoReplyOn: boolean    //if true, the user has activated Out Of Office)
     *          autoReplyInfos: \{       //if autoReplyOn is true, then this object contains the informations related to the Out Of Office)
     *              message: string     //out of office message
     *              mdate: date         //until when the OoO is activated (if known)
     *          \}
     *      \}
     *
     * NB: The Out of Office is not automatic, so it's updated every 15min for each conversation that the user has;
     */
    public calendarState: any = null;

    /** The object containing the calendar presence of the user (if available) as retrieved from server */
    public calendarStateRaw: any = null;

    // User TYPE STUFF
    // --------------------------------
    /** Specify if the Contact is associated to an Microsoft Azure Directory contact */
    public o365Flag: boolean = false;

    /** Specify if the Contact is associated to an Outlook contact */
    public outlookFlag: boolean = false;

    /** Specify if the Contact is associated to a Company Directory contact */
    public companyDirectoryFlag: boolean = false;

    /** Specify if the Contact is associated to a Personal Directory contact */
    public personalDirectoryFlag: boolean = false;

    /** Specify if the Contact is associated to a PBX phonebook entry */
    public phonebookDirectoryFlag: boolean = false;

    /** Specify if the User ia a BOT */
    public isBot: boolean = false;

    /** Specify if the User ia a Rainbow Room ( Rainbow TV) */
    public isTv: boolean = false;

    /**
     * Specify if the User is a Temporary contact.
     * Example: Call log contat for unidentified call (only external number provided)
     */
    public temp: boolean = false;

    // User BOT STUFF
    // --------------------------------
    public botDetails: string = '';
    public isBotSharing: boolean = false;

    // Rainbow ROOM (AndroidTV)
    // --------------------------------
    /** The data related to the Rainbow TV */
    public rainbowTvData: any = null;

    // Company Directory & Personal Directory STUFF
    // -----------------------------------------------
    // PMA SDKv2: TODO: Un jour maybe... Merge 'businessCompanyId'+'userId' => 'directoryContactOwnerId' ou 'OwnerId'
    /**
     * The id of the Company related to the business contact.
     * Only relevant for contacts from the Business directory.
     */
    public businessCompanyId: string = null;

    /**
     * The ID of the user associated with the personal contact
     * Only relevant for contacts from the Personal directory.
     */
    public userId: string = null;

    //  FILES/DOCUMENTS STUFF
    // --------------------------------
    public fileStorageEnabledSubject: BehaviorSubject<any>;

    /** Rainbow storage usage "enabled", "disabled", "same_as_company" */
    public useRainbowStorage?: string;

    /** External storage usage "enabled", "disabled", "same_as_company" */
    public useExternalStorage?: string;

    /** External storage identifier */
    public mainStorage?: string;

    // USER ECOSYSTEM STUFF (Company...)
    // ---------------------------------------------------------
    /** The name of the user's company or the company name of a personal/business contact */
    public companyName: string = null;

    public companyFeatures: any = null;

    /** A Company objet reprensenting the information of the company associated to the contact */
    public company: Company = null;

    /** Hide or not the offline/away presence duration (Company-level setting, User Profile) */
    public isPresenceDurationHidden: boolean = false;

    // User O365 & TEAMS STUFF
    // --------------------------------
    /** if Rainbow is used as RB + Teams app */
    public isRainbowForTeamsUser: boolean = false;

    /** The current presence shared from MS Teams (if the integration is activated for this user) */
    public msTeamsPresence: string = '';

    /** State of the Office365 subscription of the connected user only. */
    public o365SubscriptionState: O365SubscriptionState;

    // User SOFTPHONE STUFF
    // --------------------------------
    /** Specifies if user should use the new interface for softphone */
    public softphoneModeActivated: boolean = false;
    public userCanModifySoftphoneMode: boolean = false;

    public get isSoftphoneTelMode(): boolean {
        return this.softphoneModeActivated && !this.instantMessagesEnabled;
    }

    public get isSoftphoneUCMode(): boolean {
        return this.softphoneModeActivated && this.instantMessagesEnabled;
    }

    public get useSoftphoneTelPopout(): boolean {
        return this.isSoftphoneTelMode && !this.isRainbowForTeamsUser;
    }

    public get useSoftphoneUCPopout(): boolean {
        return this.isSoftphoneUCMode && !this.isRainbowForTeamsUser;
    }

    // User PBX STUFF
    // --------------------------------
    /** The ID of the PBX monitored by Rainbow where the user's phone is associated */
    public pbxId: string = null;

    public organisationId: string = null;
    public siteId: string = null;
    public systemId: string = null;
    public phoneNumberId: string = null;

    // MARKDOWN STUFF
    // --------------------------------
    // MARKDOWN infos for the contact to be shown on incoming call / card display
    private _customMarkdownInfos: string;
    public get customMarkdownInfos(): string {
        return this._customMarkdownInfos;
    }

    public set customMarkdownInfos(value: string) {
        if (this._customMarkdownInfos !== value) {
            this._customMarkdownInfos = value;
            this.rxSubject.next({ name: "customMarkdownInfos" });
        }
    }

    /** 3rd party external infos for contact */
    public thirdPartyMarkdownInfos: string;

    //  USER CAPABILITIES STUFF
    // --------------------------------

    // IMPORTANT NOTE:
    // The following Capabilities are available for SDKV2 BUT not directly or through the Getter/Setter below.
    // Knowing that these properties are Read-Only to an End-user, this also means that the setter must not be offered to the SDK.
    // For this purpose.The following Getter interfaces has been defined:  'ownFeatureCapabilities()' , 'peerFeatureCapabilities()'

    // PRESENCE capabilities
    // ----------------------
    /** The ability for a user to set the presence status to 'Invisible'. */
    public canSetInvisiblePresenceCustomisation: boolean = true;

    /** The ability for a user to change manually his presence status. */
    private _changePresenceEnabled: boolean = false;
    public set changePresenceEnabled(changePresenceEnabled: boolean) {
        if (this._changePresenceEnabled !== changePresenceEnabled) {
            this._changePresenceEnabled = changePresenceEnabled;
            this.rxSubject.next({ name: "changePresenceEnabled" });
        }
    }

    public get changePresenceEnabled() {
        return this._changePresenceEnabled;
    }

    // IM capabilities
    // ----------------------
    /** Right for a user to use instant messages (IM), to start a chat, send and receive IMs and notifications in a P2P or bubble conversation. */
    private _instantMessagesEnabled: boolean = false;

    /**
     * The ability for the user to receive emergency message.
     * A subscription to ALERT offer is required.
     */
    public isAlertNotificationEnabled: boolean = false;

    public set instantMessagesEnabled(instantMessagesEnabled: boolean) {
        if (this._instantMessagesEnabled !== instantMessagesEnabled) {
            this._instantMessagesEnabled = instantMessagesEnabled;
            this.rxSubject.next({ name: "instantMessagesEnabled" });
        }
    }

    public get instantMessagesEnabled() {
        return this._instantMessagesEnabled;
    }

    /** The ability for the user to check if an IM message has been read by the peer. */
    private _readReceiptsCustomisation: boolean = false;
    public set readReceiptsCustomisation(readReceiptsCustomisation: boolean) {
        if (this._readReceiptsCustomisation !== readReceiptsCustomisation) {
            this._readReceiptsCustomisation = readReceiptsCustomisation;
            this.rxSubject.next({ name: "readReceiptsCustomisation" });
        }
    }

    public get readReceiptsCustomisation() {
        return this._readReceiptsCustomisation;
    }

    // FILES capabilities
    // ----------------------
    /** Right to access the Rainbow file storage to upload/download/copy/share documents. */
    private _fileStorageEnabled: boolean = false;
    public set fileStorageEnabled(fileStorageEnabled: boolean) {
        if (this._fileStorageEnabled !== fileStorageEnabled) {
            this._fileStorageEnabled = fileStorageEnabled;
            this.fileStorageEnabledSubject.next(this._fileStorageEnabled);
            this.rxSubject.next({ name: "fileStorageEnabled" });
        }
    }

    public get fileStorageEnabled() {
        return this._fileStorageEnabled;
    }

    /** Right to share document (download and share documents). */
    private _fileSharingEnabled: boolean = false;
    public set fileSharingEnabled(fileSharingEnabled: boolean) {
        if (this._fileSharingEnabled !== fileSharingEnabled) {
            this._fileSharingEnabled = fileSharingEnabled;
            this.rxSubject.next({ name: "fileSharingEnabled" });
        }
    }

    public get fileSharingEnabled() {
        return this._fileSharingEnabled;
    }

    /** Right to transfer a document from one conversation (P2P or bubble) to another. */
    private _fileTransferEnabled: boolean = false;
    public set fileTransferEnabled(fileTransferEnabled: boolean) {
        if (this._fileTransferEnabled !== fileTransferEnabled) {
            this._fileTransferEnabled = fileTransferEnabled;
            this.rxSubject.next({ name: "fileTransferEnabled" });
        }
    }

    public get fileTransferEnabled() {
        return this._fileTransferEnabled;
    }

    /** The ability for the user to receive files with possibly restricted source. */
    private _receivedFileCustomisation: ReceivedFileCustomisationValue = ReceivedFileCustomisationValue.ENABLED;
    public set receivedFileCustomisation(receivedFileCustomisation: ReceivedFileCustomisationValue) {
        if (this._receivedFileCustomisation !== receivedFileCustomisation) {
            this._receivedFileCustomisation = receivedFileCustomisation;
            this.rxSubject.next({ name: "receivedFileCustomisation" });
        }
    }

    public get receivedFileCustomisation() {
        return this._receivedFileCustomisation;
    }

    // CHANNEL capabilities
    // ----------------------
    /** The ability for the user to use the 'Channels' feature, i.e. to create or be a member of channels. */
    private _channelsEnabled: boolean = false;
    public set channelsEnabled(channelsEnabled: boolean) {
        if (this._channelsEnabled !== channelsEnabled) {
            this._channelsEnabled = channelsEnabled;
            this.rxSubject.next({ name: "channelsEnabled" });
        }
    }

    public get channelsEnabled() {
        return this._channelsEnabled;
    }

    // BUBBLE capabilities
    // ----------------------
    /** The ability for the user to use the 'Bubbles' feature, i.e. to create bubbles or join bubbles (chat and bubble conference). */
    private _roomsEnabled: boolean = false;
    public set roomsEnabled(roomsEnabled: boolean) {
        if (this._roomsEnabled !== roomsEnabled) {
            this._roomsEnabled = roomsEnabled;
            this.rxSubject.next({ name: "roomsEnabled" });
        }
    }

    public get roomsEnabled() {
        return this._roomsEnabled;
    }

    /** The ability for the user to view the speaking time for each participant in a bubble conference. */
    private _useSpeakingTimeStatistics: boolean = false;
    public set useSpeakingTimeStatistics(useSpeakingTimeStatistics: boolean) {
        if (this._useSpeakingTimeStatistics !== useSpeakingTimeStatistics) {
            this._useSpeakingTimeStatistics = useSpeakingTimeStatistics;
            this.rxSubject.next({ name: "useSpeakingTimeStatistics" });
        }
    }

    public get useSpeakingTimeStatistics() {
        return this._useSpeakingTimeStatistics;
    }

    // CALL capabilities
    // ----------------------
    /** The ability for the user to record a conversation of a P2P call. */
    public recordConversationEnabled: boolean = true;

    /**
     * The ability for the user to use Web RTC audio conversation (P2P/Bubble conference call).
     * This includes the ability to make WebRTC P2P audio call, start a bubble conference call and to be joined via Web RTC audio call.
     */
    private _webrtcAudioEnabled: boolean = false;
    public set webrtcAudioEnabled(webrtcAudioEnabled: boolean) {
        if (this._webrtcAudioEnabled !== webrtcAudioEnabled) {
            this._webrtcAudioEnabled = webrtcAudioEnabled;
            this.rxSubject.next({ name: "webrtcAudioEnabled" });
        }
    }

    public get webrtcAudioEnabled() {
        return this._webrtcAudioEnabled;
    }

    /**
     * The ability for the user to use Web RTC video conversation (P2P/Bubble conference call).
     * This includes the ability to make WebRTC P2P video call, to add video in a bubble conference call and to be joined via Web RTC video call.
     */
    private _webrtcVideoEnabled: boolean = false;
    public set webrtcVideoEnabled(webrtcVideoEnabled: boolean) {
        if (this._webrtcVideoEnabled !== webrtcVideoEnabled) {
            this._webrtcVideoEnabled = webrtcVideoEnabled;
            this.rxSubject.next({ name: "webrtcVideoEnabled" });
        }
    }

    public get webrtcVideoEnabled() {
        return this._webrtcVideoEnabled;
    }

    /** The ability for the user to share his screen. */
    private _webrtcSharingEnabled: boolean = false;
    public set webrtcSharingEnabled(webrtcSharingEnabled: boolean) {
        if (this._webrtcSharingEnabled !== webrtcSharingEnabled) {
            this._webrtcSharingEnabled = webrtcSharingEnabled;
            this.rxSubject.next({ name: "webrtcSharingEnabled" });
        }
    }

    public get webrtcSharingEnabled() {
        return this._webrtcSharingEnabled;
    }

    // PROFILE & SETTINGS capabilities
    // --------------------------------
    public changeSettingsEnabled: boolean = true;

    /** The ability for the user to modify certain data relating to its name (title, firstName, lastName) in its profile. */
    public userTitleNameEnabled: boolean = false;

    /** The ability for the user to customize his/her avatar */
    public userAvatarCustomizationEnabled: boolean = true;

    /** The ability for the user to modify his entire profile. */
    private _modifyProfileEnabled: boolean = false;
    public set modifyProfileEnabled(modifyProfileEnabled: boolean) {
        if (this._modifyProfileEnabled !== modifyProfileEnabled) {
            this._modifyProfileEnabled = modifyProfileEnabled;
            this.rxSubject.next({ name: "modifyProfileEnabled" });
        }
    }

    public get modifyProfileEnabled() {
        return this._modifyProfileEnabled;
    }

    // ----------------------------------------------------------------------------------
    // IMPORTANT NOTE: Data about USER Capabilities below are NOT AVAILABLE for SDKV2
    // ----------------------------------------------------------------------------------

    //  IM Capabilities STUFF
    // --------------------------------
    /** The right to use the giphy extension */
    public giphyEnabled: boolean = true;

    //  TELEPHONY Capabilities Stuff
    // ----------------------------------
    /**
     * Ability for a user to add a participant by phone in an active conference.
     * The conference must be established before the participant's call can be made.
     */
    public dialOutCapability: boolean = false;

    /** Ability for a user to join by phone an active conference from an office phone. */
    public joinByPhoneCapability: boolean = false;

    /**
     * Ability for a user to call a participant using a PBX number via PSTN.
     * The conference must be established before the participant's call can be made.
     */
    public canCallParticipantPbxNumberEnabled?: boolean = true;

    /** Ability for a user to modify some telephony settings such as the forward activation... */
    public telephonySettingsEnabled: boolean = true;

    /** Indicates if the OXE PBX has the call busy capability */
    public oxeBusyCapability: boolean = false;

    /** Indicates if the OXE PBX has overflow capability */
    public oxePBXOverflowCapability: boolean = false;

    /** Specify whether the connected user has an attendant profile */
    public attendantConsoleEnabled: boolean = false;

    /** Specify whether the user has the console supervision group role. */
    public supervisorConsoleEnabled: boolean = false;

    /** Capability for a user to use the computer mode. */
    public useComputerMode?: boolean = true;

    /** Capability for a user to use the other phone mode.*/
    public useOtherPhoneMode?: boolean = true;

    /** Capability for a user to change the order of calls in telephony phone numbers menu.*/
    public showFirstPhoneCalls?: boolean = false;

    //  SUPERVISION CONSOLE Capabilities Stuff
    // ------------------------------------------
    private _supervisionCapabilities: any = null;
    public set supervisionCapabilities(supervisionCapabilities: any) {
        if (this._supervisionCapabilities !== supervisionCapabilities) {
            this._supervisionCapabilities = supervisionCapabilities;
            this.rxSubject.next({ name: "supervisionCapabilities" });
        }
    }

    public get supervisionCapabilities() {
        return this._supervisionCapabilities;
    }

    /*
     * Supervision console call capabilites
     * Mainly the ability for to propose 'make call' and pickup buttons.
     */
    private _supervisionCallCapabilities: any = null;
    public set supervisionCallCapabilities(callCapabilities: any) {
        if (this._supervisionCallCapabilities !== callCapabilities) {
            this._supervisionCallCapabilities = callCapabilities;
            this.rxSubject.next({ name: "supervisionCallCapabilities" });
        }
    }

    public get supervisionCallCapabilities() {
        return this._supervisionCallCapabilities;
    }

    //  OTHER FEATURES Capabilities Stuff
    // ----------------------------------
    /** Ability for a user to download Rainbow application. */
    public canDownloadAppCustomisation: boolean = true;

    /** Ability for a user to access to Rainbow store. */
    public canAccessStoreCustomisation: boolean = true;

    /** Ability for a user to record a bubble conference. */
    public conferenceRecordingEnabled: boolean = false;

    /** Ability for a user to access to Rainbow help center. */
    public canAccessHelpCenterCustomisation: boolean = true;

    /** Ability for a user to access to the FAQ. */
    public canAccessFaqCustomisation: boolean = true;

    /** Ability for a user to use "What's new" feature.*/
    public canAccessWhatsNew: boolean = true;

    /** Ability for a user to use "Tasks" feature.*/
    public canUseTaskCustomisation: boolean = true;

    /** Ability for a user to use "Calendar" feature.*/
    public canSynchCalendar: boolean = true;

    /** Ability for a user to use "Transcription" feature.*/
    public canUseTranscriptionCustomisation: boolean = false;

    /** Ability for a user to send report about a problem.*/
    public canUseSendReportCustomisation: boolean = true;

    /** Ability for a user to test its configuration (connectivity and audio quality).*/
    public canUseTestConfigCustomisation: boolean = true;

    /**
     * RQRAINB-13455 [TECHNICAL] Keep the customization useRoomCustomisation as disabled for Rainbow Voice users
     * For Rainbow Voice users only, indicates that a user has the right to use Bubbles for telephony.
     */
    public roomsAsVoiceEnabled: boolean = true;

    // END OF USER CAPABILITIES STUFF

    //  E-LEARNING STUFF
    // --------------------------------
    /** The right to access elearning section */
    public eLearningCustomisation: boolean = false;

    /** The right to earn badges in elearning section */
    public eLearningGamificationCustomisation: boolean = false;

    //  FORWARD STUFF
    // --------------------------------
    private _forwards: TelephonyForward[] = null;
    public set forwards(forwards: TelephonyForward[]) {
        this._forwards = forwards;
        this.rxSubject.next({ name: "forwards" });
    }

    public get forwards(): TelephonyForward[] {
        return this._forwards;
    }

    // CUSTOM STATUS STUFF
    // ----------------------------------
    /**
     * updateCustomStatus
     */
    public updateCustomStatus(status: any) {
        this.customStatus = status;
    }

    //  PERSONAL ROUTINES STUFF
    // --------------------------------
    private _personalRoutines: PersonalRoutine[] = null;
    public set personalRoutines(personalRoutines: PersonalRoutine[]) {
        this._personalRoutines = personalRoutines;
        this.rxSubject.next({ name: "personalRoutines" });
    }

    public get personalRoutines(): PersonalRoutine[] {
        return this._personalRoutines;
    }

    // PMA SDKv2: TODO:  Un jour maybe... Rename 'outOfOffice => 'currentPersonalRoutineStatusMessage'
    private _outOfOffice: { active: boolean; message: string; } = { active: false, message: "" };
    public get outOfOffice(): { active: boolean; message: string; } {
        return this._outOfOffice;
    }

    public set outOfOffice(value: { active: boolean; message: string; }) {
        if (this._outOfOffice !== value) {
            this._outOfOffice = value;
            this.rxSubject.next({ name: "outOfOffice" });
        }
    }

    //  SUPERVISION Stuff
    // ----------------------------------
    private _supervisionDevices: any;

    private _supervisionState: string = null;
    public set supervisionState(supervisionState: any) {
        const previousState = this._supervisionState;
        this._supervisionState = supervisionState;
        if (previousState !== supervisionState) {
            this.rxSubject.next({ name: "supervisionState" });
        }
    }

    public get supervisionState() {
        return this._supervisionState;
    }

    private _supervisionCallInfos: any[] = null;
    public get supervisionCallInfos(): any[] {
        return this._supervisionCallInfos;
    }

    public set supervisionCallInfos(supervisionCallInfos: any[]) {
        const prevCallInfos = this._supervisionCallInfos;
        this._supervisionCallInfos = supervisionCallInfos;

        // retrieve call notification info from previous callInfos for each call in new callInfos
        (this._supervisionCallInfos || []).forEach((newCallInfo) => {
            const prevCallInfoFound = (prevCallInfos || []).find(prevCallInfo => prevCallInfo.callId === newCallInfo.callId);
            if (prevCallInfoFound) {
                newCallInfo.notified = prevCallInfoFound.notified;
            }
        });
        this.rxSubject.next({ name: "supervisionCallInfos" });
    }

    // CCD STUFF
    // --------------------------------
    public ccdPolicy: { onBusyWithdraw: boolean; onDndWithdraw: boolean; onDndIgnoreVoipCalls: boolean; withdrawReason: string; } = { onBusyWithdraw: true, onDndWithdraw: false, onDndIgnoreVoipCalls: true, withdrawReason: "0" };
    public ccdAgent: CcdAgent;

    //  TELEPHONY STUFF
    // --------------------------------
    /**
     * Phone type name for an extension behind an Rainbow Hybrid PBX
     * Standard device name for an extension behind a Rainbow Hub PBX (SipWise subscriber)
     */
    public systDeviceName: string = null;

    /**
     * The telephone user is connected to a virtual device (isMonoDevice) on the PBX side (REX, any device).
     * Useful only for the connected user.
     */
    public isVirtualTerm: boolean = false;

    /** Mute ringing on incoming calls to the office extension (OXE PBX only)*/
    public silentRinging: boolean = false;

    /**
     * Allows WebRTC calls only if the user has a mobile presence.
     * Use case: COBRI user
     */
    public useWebRTCOnlyIfMobileLogged: boolean = false;

    public oxeTrunks: ConnectedUserOxeTrunk[] = []; //RQRAINB-12331
    public oxePreferredTrunk: number = -1; //the index of the preferred oxe trunk / -1 is the default value corresponding to default oxe trunk

    //  UI STUFF
    // --------------------------------
    /** duration of the IM set by the company admin */
    public imPopupDuration;

    /** The right to use the new UI */
    public newUiEnabled: boolean = false;

    public generalizedAttendantLookForUsers: boolean = false; //RQRAINB-9428

    //the File Descriptor ID related to the custom manifest file on server
    public selectedManifestId;

    //  MFA STUFF
    // --------------------------------
    // Multi-factor authentication data
    public mfaRainbowAuth: { mfaStatus: MultiFactorAuthStatus; mfaTrustedApps: any[]; mfaType: MultiFactorAuthType; } = null;
    public getMfaStatus(): MultiFactorAuthStatus {
        return this.mfaRainbowAuth?.mfaStatus;
    }

    public setMfaStatus(newStatus: MultiFactorAuthStatus) {
        this.mfaRainbowAuth.mfaStatus = newStatus;
    }

    // User AI CAPABILITIES STUFF
    // --------------------------------
    /** Specifies what the user can do using AI in RB */
    public recordingTranscription: boolean = false;
    public liveTranscription: boolean = false;
    public translateDoc: boolean = false;
    public translationDocLanguages?: { iso: string; native: string; translated: string; inverted: boolean; }[] = [];
    public translationDocFormats?: string[] = [];
    public recordingLanguages?: { iso: string; native: string; translated: string; inverted: boolean; }[] = [];
    public liveLanguages?: { iso: string; native: string; translated: string; inverted: boolean; }[] = [];

    public get canRecordingTranscription(): boolean {
        return this.recordingTranscription;
    }

    public get canLiveTranscription(): boolean {
        return this.liveTranscription;
    }

    public get canTranslateDoc(): boolean {
        return this.translateDoc;
    }

    public get getTranslationDocLanguages(): { iso: string; native: string; translated: string; inverted: boolean; }[] {
        return this.translationDocLanguages;
    }

    public get getTranslationDocFormats(): string[] {
        return this.translationDocFormats;
    }

    public get getRecordingLanguages(): { iso: string; native: string; translated: string; inverted: boolean; }[] {
        return this.recordingLanguages;
    }

    public get getLiveLanguages(): { iso: string; native: string; translated: string; inverted: boolean; }[] {
        return this.liveLanguages;
    }

    // ========================================================================
    //                       METHODES
    // ========================================================================

    // --------------------------------------------------------------------------------------------
    //                          CONTACT INSTANCE PUBLIC API - USED BY SDKv2
    // --------------------------------------------------------------------------------------------

    /**
     * Subscribe to updates on the current contact (all events are of RBEvent type \{name: string, data:any \});
     * Here's a list of events received :
     *    name: 'update'        means...  update of any information of the contact (settings, personnal information, network/roster status, etc)
     *    name: 'calendar'      means...  update of the user's calendar information
     *    name: 'presence'      means...  update of the user's presence status
     *    name: 'outOfOffice'   means...  update of the user's out of office status
     *    name: 'roster'        means...  update of the user's roster change
     *    NB There might be some events that are not documented here, they're send on updates of different properties of the contact.
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventNames - Array of event(s) to listen
     */
    public subscribe(handler: any, eventNames: string[] = []): Subscription {
        //allow only events that are declared in the eventNames
        return this.rxSubject.pipe(filter(rbEvent => (!eventNames.length || eventNames.indexOf(rbEvent.name) !== -1))).subscribe((event: RBEvent) => {
            try { handler(event); } catch {}
        });
    }

    public sendEvent(name: UserEvents, data?: any): void {
        this.rxSubject.next(RBEvent.create<UserEvents, any>(name, data));
    }

    public isConnectedUser(): boolean {
        return this.dbId === this.contactService.userContact.dbId;
    }

    public isDirectoryContact(): boolean {
        return (this.personalDirectoryFlag || this.companyDirectoryFlag
            || this.o365Flag || this.phonebookDirectoryFlag || this.outlookFlag);
    }

    public isUserTypeOf(userType: UserType): boolean {
        return (this.userType === userType);

    }

    public hasRole(userRole: UserRole): boolean {
        return this.roles.indexOf(userRole) >= 0;
    }

    /**
     * Indicates wether the user is a telephony user or not.
     * A telephony user is a user with an extension number allocated on a PBX, i.e. a Rainbow Hub or Rainbow Hybrid (OXO or OXE) telephone system.
     */
    public isTelephonyUser(): boolean {
        return Boolean(this.pbxId);
    }

    /**
     * Indicates wether the user is a Rainbow Hub user or not.
     * A Rainbow Hub user is a user with a Voice licence and an extension number allocated on a Cloud PBX.
     */
    public isRainbowHubUser(): boolean {
        return this.systDeviceName === Contact.SIPWISE_RVCP;
    }

    /**
     * Indicates wether the user is an Rainbow Hybrid user or not.
     * An Rainbow Hybrid user is a user with an extension number allocated on a Hybrid PBX (OXE/OXO/Third party)
     */
    public isRainbowHybridUser(): boolean {
        return this.isTelephonyUser() && this.systDeviceName !== Contact.SIPWISE_RVCP;
    }

    // --------------------------------------------------------------------------------------------
    //              CONTACT INSTANCE PUBLIC API - Internal management - NOT used by SDKv2
    // --------------------------------------------------------------------------------------------

    /**
     *                              CONTACT INSTANCE CONSTRUCTOR
     * WARNING: Not all properties available in the Contact class are initialized in this method.
     */
    constructor(jid: string = null, name: string = null, company: Company = null) {

        // User INTERNAL DATA
        this.id = jid;
        this.dbId = null;
        this.jid = jid;
        this.fullJid = null;
        this.jidtel = null;

        // User LIFE CYCLE DATA
        this.initialized = false;
        this.subscription = "none";
        this.ask = "none";
        this.roster = false;
        this.confId = "";
        this.conversation = null;
        this.capabilities = null;
        this.denySelfRegister = false;

        // User STATUS DATA
        this.status = "unknown";
        this.message = "";
        this.telStatus = "";
        this.imStatus = "offline";
        this.oldImStatus = null;
        this.imStatusStamp = [];
        this.resources = {};
        this.mobileResource = null;
        this.lastActivityDate = new BehaviorSubject<Date | null>(null);

        // User IDENTITY DATA
        this.loginEmail = "";
        this.lastname = "";
        this.firstname = "";
        this.initials = "";
        this.name = { value: this._displayName };
        this.nameForLogs = "";
        this._displayName = name ? name : jid;
        this.nickname = "";
        this.title = "";
        this.jobTitle = "";
        this.language = null;
        this.country = "";
        this.timezone = "";
        this.tags = null;
        this.nameUpdatePrio = Contact.NameUpdatePrio.MAX_UPDATE_PRIO;

        // User AVATAR DATA
        this.defaultAvatar = null;
        this._avatar = null;
        this.avatarSrc = null;

        // User PHONE NUMBERS DATA
        this.phonePro = "";
        this.phoneProCan = "";
        this.secondaryDdiNumbers = []; //RQRAINB-11807
        this.mobilePro = "";
        this.mobileProCan = "";
        this.isMobileProShared = false;
        this.phonePerso = "";
        this.phonePersoCan = "";
        this.isPhonePersoShared = false;
        this.mobilePerso = "";
        this.mobilePersoCan = "";
        this.isMobilePersoShared = false;
        this.phonePbx = "";
        this.phoneInternalNumber = "";
        this.otherPhoneNumbers = [];

        // User Emails DATA
        this.emailPro = "";
        this.emailPerso = "";

        // User NOTES DATA
        this.note1 = "";
        this.note2 = "";

        // User POSTAL ADDRESS DATA
        this.department = "";
        this.postalCode = "";
        this.state = "";
        this.street = "";
        this.city = "";

        // User CUSTOM & GENERAL data
        this.userInfo1 = null;
        this.userInfo2 = null;

        // User CALENDAR DATA
        this.calendarState = {};
        this.calendarStateRaw = {};

        // User TYPE DATA
        this.temp = false;
        this.isTv = false;

        // Company Directory & Personal Directory DATA
        this.businessCompanyId = "";
        this.userId = "";

        // Rainbow ROOM (AndroidTV) DATA
        this.rainbowTvData = {};

        // User ECOSYSTEM DATA (Company...)
        this.companyName = "";
        this.company = company;
        this.isInDefaultCompany = false;

        // CAPABILITIES DATA (from User and from its Company)
        this.canSetInvisiblePresenceCustomisation = true;
        this._changePresenceEnabled = true;
        this._instantMessagesEnabled = true;
        this._readReceiptsCustomisation = true;
        this.isAlertNotificationEnabled = false;
        this._fileStorageEnabled = true;
        this._fileSharingEnabled = true;
        this._fileTransferEnabled = true;
        this.fileStorageEnabledSubject = new BehaviorSubject(true);
        this._receivedFileCustomisation = ReceivedFileCustomisationValue.ENABLED;
        this._webrtcAudioEnabled = true;
        this._webrtcVideoEnabled = true;
        this._webrtcSharingEnabled = true;
        this._modifyProfileEnabled = true;
        this.userTitleNameEnabled = true;
        this._roomsEnabled = true;
        this.conferenceRecordingEnabled = false;
        this._useSpeakingTimeStatistics = true;
        this._channelsEnabled = true;
        this.roomsAsVoiceEnabled = true;
        this.giphyEnabled = true;

        //  FILES/DOCUMENTS DATA
        this.useRainbowStorage = undefined;
        this.useExternalStorage = undefined;
        this.mainStorage = undefined;

        // User PBX DATA
        this.pbxId = "";

        // TELEPHONY DATA
        this.isVirtualTerm = false;
        this.systDeviceName = null;
        this.supervisorConsoleEnabled = false;
        this.useComputerMode = false;
        this.useOtherPhoneMode = false;
        this.canCallParticipantPbxNumberEnabled = true;
        this.useWebRTCOnlyIfMobileLogged = false;

        // ADMIN DATA
        this.roles = [];
        this.adminType = null;

        // User TEAMS DATA
        this.msTeamsPresence = '';

        //  E-LEARNING DATA
        this.eLearningCustomisation = false;
        this.eLearningGamificationCustomisation = false;

        // UI DATA
        this.newUiEnabled = false;
    }

    /**
     *                              CONTACT INSTANCE GLOBAL UPDATE
     * Update of some properties of a user instance following reception of new data from the server.
     * @param userData - Partial set of user properties as received from the server
     */
    public updateFromUserData(userData: any): void {
        this.isBot = false;
        this.isBotSharing = false;

        if (userData.id !== undefined) { this.dbId = userData.id; }
        if (userData.loginEmail !== undefined) { this.loginEmail = userData.loginEmail; }
        if (userData.firstName !== undefined) { this.firstname = userData.firstName; }
        if (userData.lastName !== undefined) { this.lastname = userData.lastName; }
        if (userData.nickName !== undefined) { this.nickname = userData.nickName; }
        if (userData.title !== undefined) { this.title = userData.title; }
        if (userData.jobTitle !== undefined) { this.jobTitle = userData.jobTitle; }
        if (userData.organisationId !== undefined) { this.organisationId = userData.organisationId; }
        if (userData.siteId !== undefined) { this.siteId = userData.siteId; }
        if (userData.country !== undefined) { this.country = userData.country; }
        if (userData.timezone !== undefined) { this.timezone = userData.timezone; }
        if (userData.roles !== undefined) { this.roles = userData.roles; }
        if (userData.adminType !== undefined) { this.adminType = userData.adminType; }
        if (userData.isTerminated !== undefined) { this.isTerminated = userData.isTerminated; }
        if (userData.isInDefaultCompany !== undefined) { this.isInDefaultCompany = userData.isInDefaultCompany; }
        if (userData.lastAvatarUpdateDate !== undefined) { this.lastAvatarUpdateDate = userData.lastAvatarUpdateDate; }
        if (userData.isInitialized !== undefined) { this.initialized = userData.isInitialized; }
        if (userData.guestMode !== undefined) { this.guestMode = userData.guestMode; }
        if (userData.companyNameOfGuest) { this.companyNameOfGuest = userData.companyNameOfGuest; }
        if (userData.isTv !== undefined) { this.isTv = userData.isTv; }
        if (userData.tags !== undefined) { this.tags = userData.tags; }
        if (userData.department !== undefined) { this.department = userData.department; }
        if (userData.customData !== undefined) { this.customData = userData.customData; }
        if (userData.denySelfRegister !== undefined) { this.denySelfRegister = userData.denySelfRegister; }
        if (userData.userInfo1 !== undefined) { this.userInfo1 = userData.userInfo1; }
        if (userData.userInfo2 !== undefined) { this.userInfo2 = userData.userInfo2; }
        if (userData.customStatus !== undefined) { this.customStatus = userData.customStatus; } else this.customStatus = null;

        // Handle jids
        if (userData.jid_im !== undefined) {
            this.id = userData.jid_im;
            this.jid = userData.jid_im;
            if (userData.jid_tel !== undefined) {
                this.jidtel = userData.jid_tel;
            }

            if (userData.jid_im?.indexOf("tv_") === 0) {
                this.isTv = true;
            }
        }

        // Company field
        if (!this.company || this.company.id !== userData.companyId) {
            this.company = Company.create(userData.companyId, userData.companyName);
            this.companyName = userData.companyName;
        }

        // UCaaS customization fields
        if (userData.fileSharingCustomisation !== undefined) {
            this._fileSharingEnabled = userData.fileSharingCustomisation === "enabled";
        }
        if (userData.fileTransferCustomisation !== undefined) {
            this._fileTransferEnabled = userData.fileTransferCustomisation === "enabled";
        }
        if (userData.fileStorageCustomisation !== undefined) {
            this.fileStorageEnabled = userData.fileStorageCustomisation === "enabled";
        }
        if (userData.userTitleNameCustomisation !== undefined) {
            this.userTitleNameEnabled = userData.userTitleNameCustomisation === "enabled";
        }
        if (userData.instantMessagesCustomisation !== undefined) {
            this._instantMessagesEnabled = userData.instantMessagesCustomisation === "enabled";
        }
        if (userData.overridePresenceCustomisation !== undefined) {
            this._changePresenceEnabled = userData.overridePresenceCustomisation === "enabled";
        }
        if (userData.useChannelCustomisation !== undefined) {
            this._channelsEnabled = userData.useChannelCustomisation === "enabled";
        }
        if (userData.useRoomCustomisation !== undefined) {
            this._roomsEnabled = userData.useRoomCustomisation === "enabled";
        }
        if (userData.useRoomAsRBVoiceUser !== undefined) {
            this.roomsAsVoiceEnabled = userData.useRoomAsRBVoiceUser === "enabled";
        }
        if (userData.canSetInvisiblePresenceCustomisation !== undefined) {
            this.canSetInvisiblePresenceCustomisation = userData.canSetInvisiblePresenceCustomisation === "enabled";
        }
        if (userData.useWebRTCAudioCustomisation !== undefined) {
            this._webrtcAudioEnabled = userData.useWebRTCAudioCustomisation === "enabled";
        }
        if (userData.useWebRTCVideoCustomisation !== undefined) {
            this._webrtcVideoEnabled = userData.useWebRTCVideoCustomisation === "enabled";
        }
        if (userData.useScreenSharingCustomisation !== undefined) {
            this._webrtcSharingEnabled = userData.useScreenSharingCustomisation === "enabled";
        }
        if (userData.userProfileCustomisation !== undefined) {
            this._modifyProfileEnabled = userData.userProfileCustomisation === "enabled";
        }
        if (userData.useGifCustomisation !== undefined) {
            this.giphyEnabled = userData.useGifCustomisation === "enabled";
        }
        if (userData.changeTelephonyCustomisation !== undefined) {
            this.telephonySettingsEnabled = userData.changeTelephonyCustomisation === "enabled";
        }
        if (userData.recordingConversationCustomisation !== undefined) {
            this.recordConversationEnabled = userData.recordingConversationCustomisation === "enabled";
        }
        if (userData.changeSettingsCustomisation !== undefined) {
            this.changeSettingsEnabled = userData.changeSettingsCustomisation === "enabled";
        }
        if (userData.useSpeakingTimeStatistics !== undefined) {
            this.useSpeakingTimeStatistics = userData.useSpeakingTimeStatistics === "enabled";
        }
        if (userData.readReceiptsCustomisation !== undefined) {
            this.readReceiptsCustomisation = userData.readReceiptsCustomisation === "enabled";
        }
        if (userData.meetingRecordingCustomisation !== undefined) {
            this.conferenceRecordingEnabled = userData.meetingRecordingCustomisation === "enabled";
        }
        if (userData.useWebRTCOnlyIfMobileLoggedCustomisation !== undefined) {
            this.useWebRTCOnlyIfMobileLogged = userData.useWebRTCOnlyIfMobileLoggedCustomisation === "enabled";
        }
        if (userData.canDownloadAppCustomisation !== undefined) {
            this.canDownloadAppCustomisation = userData.canDownloadAppCustomisation === "enabled";
        }
        if (userData.canAccessHelpCenterCustomisation !== undefined) {
            this.canAccessHelpCenterCustomisation = userData.canAccessHelpCenterCustomisation === "enabled";
        }
        if (userData.canAccessFaqCustomisation !== undefined) {
            this.canAccessFaqCustomisation = userData.canAccessFaqCustomisation === "enabled";
        }
        if (userData.canAccessWhatsNew !== undefined) {
            this.canAccessWhatsNew = userData.canAccessWhatsNew === "enabled";
        }
        if (userData.canAccessStoreCustomisation !== undefined) {
            this.canAccessStoreCustomisation = userData.canAccessStoreCustomisation === "enabled";
        }
        if (userData.canUseTaskCustomisation !== undefined) {
            this.canUseTaskCustomisation = userData.canUseTaskCustomisation === "enabled";
        }
        if (userData.canSynchCalendar !== undefined) {
            this.canSynchCalendar = userData.canSynchCalendar === "enabled";
        }
        if (userData.canUseTranscriptionCustomisation !== undefined) {
            this.canUseTranscriptionCustomisation = userData.canUseTranscriptionCustomisation === "enabled";
        }
        if (userData.userAvatarCustomisation !== undefined) {
            this.userAvatarCustomizationEnabled = userData.userAvatarCustomisation === "enabled";
        }
        if (userData.canUseSendReportCustomisation !== undefined) {
            this.canUseSendReportCustomisation = userData.canUseSendReportCustomisation === "enabled";
        }
        if (userData.canUseTestConfigCustomisation !== undefined) {
            this.canUseTestConfigCustomisation = userData.canUseTestConfigCustomisation === "enabled";
        }

        if (userData.imPopupDuration) {
            this.imPopupDuration = userData.imPopupDuration;
        }

        if (userData.eLearningCustomisation !== undefined) {
            this.eLearningCustomisation = userData.eLearningCustomisation === "enabled";
            if (this.eLearningCustomisation) {
                if (userData.eLearningGamificationCustomisation !== undefined) {
                    this.eLearningGamificationCustomisation = userData.eLearningGamificationCustomisation === "enabled";
                }
            }
        }

        if (userData.useOtherPhoneMode !== undefined) {
            this.useOtherPhoneMode = userData.useOtherPhoneMode === "enabled";
        }

        if (userData.useComputerMode !== undefined) {
            this.useComputerMode = userData.useComputerMode === "enabled";
        }

        if (userData.canCallParticipantPbxNumberCustomisation !== undefined) {
            // Possible values : "enabled" / "internal" / "national" / "disabled"
            this.canCallParticipantPbxNumberEnabled = userData.canCallParticipantPbxNumberCustomisation !== "disabled";
        }

        if (userData.isAlertNotificationEnabled !== undefined) {
            this.isAlertNotificationEnabled = userData.isAlertNotificationEnabled;
        }

        if (userData.forwards) {
            // Voice forwards
            this._forwards = userData.forwards.map((userDataForward: any) => {
                return TelephonyForward.createForwardFromVoiceUserData(userDataForward);
            });
        }

        if (userData.forward) {
            // Hybrid forward
            this._forwards = [TelephonyForward.createForwardFromHybridData(userData.forward)];
        }

        if (userData.personalRoutines) {
            this._personalRoutines = userData.personalRoutines.map((userDataRoutine: any) => {
                return PersonalRoutine.createRoutineFromData(userDataRoutine);
            });
        }

        if (userData.capabilityOxeBusy) {
            this.oxeBusyCapability = userData.capabilityOxeBusy;
        }

        if (userData.useSoftPhoneMode !== undefined) {
            this.softphoneModeActivated = (userData.useSoftPhoneMode === "enabled" && userData.customData?.isSoftphone) || userData.useSoftPhoneMode === "forced";
            this.userCanModifySoftphoneMode = userData.useSoftPhoneMode === "enabled";
        }

        if (userData.useTeamsMode !== undefined) {
            this.isRainbowForTeamsUser = userData.useTeamsMode === "enabled";
        }

        if (userData.receivedFileCustomisation !== undefined) {
            this.receivedFileCustomisation = userData.receivedFileCustomisation;
        }

        if (userData.hidePresenceDurationCustomisation !== undefined) {
            this.isPresenceDurationHidden = (userData.hidePresenceDurationCustomisation === "enabled");
        }

        if (userData.showFirstPhoneCalls !== undefined) {
            this.showFirstPhoneCalls = userData.showFirstPhoneCalls === "enabled";
        }
        // Update emails
        if (userData.emails !== undefined) {
            this.emailPerso = ""; //PR_14671
            userData.emails.forEach((email) => {
                switch (email.type) {
                    case "work":
                        this.emailPro = email.email;
                        break;
                    case "home":
                        this.emailPerso = email.email;
                        break;
                    default:
                        break;
                }
            });
        }

        // Custom data
        if (!this.customData) {
            this.customData = {};
        }

        // Update phone numbers
        if (userData.phoneNumbers !== undefined) {
            this.phonePro = this.phoneProCan = "";
            this.phonePbx = "";
            this.phoneInternalNumber = "";
            this.pbxId = "";
            this.mobilePro = this.mobileProCan = "";
            this.phonePerso = this.phonePersoCan = "";
            this.mobilePerso = this.mobilePersoCan = "";
            this.voicemailNumber = "";
            this.systemId = "";
            this.phoneNumberId = "";
            this.hasPhoneNumber = false;
            this.hasPhoneProDdiNumber = false;
            this.ccdAgent = undefined;
            this.secondaryDdiNumbers = []; //RQRAINB-11807

            this.redirectNumber = "";
            this.isRedirectNumberEditable = true;
            this.silentRinging = false;

            userData.phoneNumbers.forEach((phoneNumber) => {
                const number = phoneNumber.number;
                const numberCan = phoneNumber.numberE164;
                const deviceType = phoneNumber.deviceType;
                this.hasPhoneNumber = true;
                switch (phoneNumber.type) {
                    case "work":
                        if (deviceType === "landline") {
                            if (phoneNumber.isCloudPbxDDI) { //RQRAINB-11807++
                                if (!phoneNumber.isAssignedAsSecondaryCloudPbxDDI) { //it is the main ddi number
                                    this.phonePro = phoneNumber.number;
                                    this.phoneProCan = phoneNumber.numberE164;
                                    this.hasPhoneProDdiNumber = true;
                                // console.log(" user '"+ this.dbId + "' PhoneNumber DDI number '"+ this.phonePro + "' numberE164 '"+ this.phoneProCan + "'");
                                } else { // It is a secondary ddi number
                                    const secondaryDdiNumberItem = { phonePro: phoneNumber.number, phoneProCan: phoneNumber.numberE164, secondaryDdiOrder: phoneNumber.secondaryDdiOrder };
                                    this.secondaryDdiNumbers.push(secondaryDdiNumberItem);
                                } //RQRAINB-11807--
                            } else {
                                if (!this.hasPhoneProDdiNumber) {
                                    this.phonePro = number || "";
                                    this.phoneProCan = numberCan || "";
                                    // console.log("user '"+ this.dbId + "' PhoneNumber number '"+ this.phonePro + "' numberE164 '"+ this.phoneProCan + "'");
                                }
                            }
                            if (phoneNumber.isFromSystem) {
                                if (phoneNumber.ccdRole !== "secondary") { // role "main" or undefined: this is the main system phonenumber
                                    this.phonePbx = phoneNumber.shortNumber;
                                    if (phoneNumber.internalNumber) {
                                        this.phoneInternalNumber = phoneNumber.internalNumber;
                                        if (
                                            phoneNumber.deviceName === "Remote Extension"
                                            || phoneNumber.deviceName === "Any Device"
                                            || phoneNumber.deviceName === "ANY DEVICE Virtual Terminal"
                                            || phoneNumber.deviceName.search("UA VIRTUAL") === 0
                                        ) {
                                            this.isVirtualTerm = true;
                                        } else {
                                            this.isVirtualTerm = false;
                                        }
                                    }
                                    this.systDeviceName = phoneNumber.deviceName ? phoneNumber.deviceName : null;
                                    this.pbxId = phoneNumber.pbxId;
                                    this.voicemailNumber = phoneNumber.voiceMailNumber;
                                    this.systemId = phoneNumber.systemId;
                                    this.phoneNumberId = phoneNumber.phoneNumberId;
                                    if (phoneNumber.number) {
                                        this.hasPhoneProDdiNumber = true; // so that phonePro of a "work/landine non-system" phonenumber does not override the phonePro of a "work/landline system" phonenumber
                                    }
                                    this.redirectNumber = phoneNumber.redirectNumber !== undefined ? phoneNumber.redirectNumber : ""; //RQRAINB-9069
                                    this.isRedirectNumberEditable = phoneNumber.isRedirectNumberEditable !== undefined ? phoneNumber.isRedirectNumberEditable : true; //RQRAINB-9069
                                    this.silentRinging = phoneNumber.silentRinging !== undefined ? phoneNumber.silentRinging : false;

                                    if (phoneNumber.ccdRole === "main") {
                                        this.ccdAgent = this.ccdAgent || { mode: undefined, agentPhoneNumber: "" };
                                        this.ccdAgent.mode = phoneNumber.ccdMode; // default / mixed / fixed
                                        if (this.ccdAgent.mode === ccdModes.DEFAULT || this.ccdAgent.mode === ccdModes.FIXED) {
                                            this.ccdAgent.agentPhoneNumber = phoneNumber.shortNumber;
                                        }
                                        if (this.ccdAgent.mode !== ccdModes.DEFAULT) {
                                            // For default ccd usecase, isVirtualTerm is based on deviceName (computed above)
                                            this.isVirtualTerm = true;
                                        }
                                    } else if (!phoneNumber.ccdRole && phoneNumber.userType === "CCD agent") {
                                        // To be compatible with old backend without ccdRole
                                        this.ccdAgent = { mode: ccdModes.DEFAULT, agentPhoneNumber: phoneNumber.shortNumber };
                                    }
                                } else if (phoneNumber.ccdRole === "secondary") {
                                    this.isVirtualTerm = true;
                                    this.ccdAgent = this.ccdAgent || { mode: undefined, agentPhoneNumber: "" };
                                    if (phoneNumber.ccdMode === ccdModes.MIXED) {
                                        this.ccdAgent.agentPhoneNumber = phoneNumber.shortNumber;
                                    }
                                }
                            }
                        }
                        if (deviceType === "mobile") {
                            this.mobilePro = number;
                            this.isMobileProShared = phoneNumber.isVisibleByOthers;
                            this.mobileProCan = numberCan;
                        }
                        break;
                    case "home":
                        if (deviceType === "landline") {
                            this.phonePerso = number;
                            this.isPhonePersoShared = phoneNumber.isVisibleByOthers;
                            this.phonePersoCan = numberCan;
                        }
                        if (deviceType === "mobile") {
                            this.mobilePerso = number;
                            this.isMobilePersoShared = phoneNumber.isVisibleByOthers;
                            this.mobilePersoCan = numberCan;
                        }
                        break;
                    case "login":
                        this.loginPhone = numberCan;
                        break;
                    default:
                        break;
                }
            });
            this.phoneNumbersInitialized = true;
        }

        // Update outOfOffice STUFF
        if (userData.outOfOffice !== undefined) {
            this.outOfOffice = userData.outOfOffice;
        }

        // Webrtc conference dialout capability for hybrid system
        if (userData.isCallToPbxAuthorized !== undefined) {
            this.dialOutCapability = userData.isCallToPbxAuthorized;
        }

        // Webrtc conference joinyByPhone capability for hybrid system
        if (userData.isCallFromPbxAuthorized !== undefined) {
            this.joinByPhoneCapability = userData.isCallFromPbxAuthorized;
        }

        // Fallback overflow capability for oxe system
        if (userData.capabilityOxeOverflow !== undefined) {
            this.oxePBXOverflowCapability = userData.capabilityOxeOverflow;
        }

        // Multi-factor authentication data
        if (userData.mfaRainbowAuth) {
            this.mfaRainbowAuth = MultiFactorAuthRainbowAuth.createMFARainbowAuthFromData(userData.mfaRainbowAuth);
        }

        //ccdPolicy data
        if (userData.ccdPolicy !== undefined) {
            this.ccdPolicy.onBusyWithdraw = userData.ccdPolicy.onBusyCcdWithdraw;
            this.ccdPolicy.onDndWithdraw = userData.ccdPolicy.onDndCcdWithdraw;
            this.ccdPolicy.withdrawReason = userData.ccdPolicy.withdrawReason.toString();
            this.ccdPolicy.onDndIgnoreVoipCalls = userData.ccdPolicy.ignoreVoipCalls;
        }

        // Storage capabilities
        if (userData.useRainbowStorage !== undefined && !this.useRainbowStorage) this.useRainbowStorage = userData.useRainbowStorage;
        if (!userData.useRainbowStorage) this.useRainbowStorage = 'enabled';
        if (userData.useExternalStorage !== undefined && !this.useExternalStorage) this.useExternalStorage = userData.useExternalStorage;
        if (!userData.useExternalStorage) this.useExternalStorage = 'disabled';
        if (userData.mainStorage !== undefined && !this.mainStorage) this.mainStorage = userData.mainStorage;
        if (!userData.mainStorage) this.mainStorage = 'Rainbow Storage';

        if (userData.selectedManifestId) { this.selectedManifestId = userData.selectedManifestId; }

        if (userData?.msGraph?.subscriptionState !== undefined) {
            this.o365SubscriptionState = userData.msGraph.subscriptionState;
        }

        // Compute display name
        this.computeDisplayName();

        // Update avatar
        this.updateAvatar();
    }

    // --------------------------------------------------------------------------------------------
    //                          CONTACT INSTANCE PUBLIC APIs NOT USED BY SDKv2
    // --------------------------------------------------------------------------------------------

    // -- AVATAR STUFF
    // --------------------------------
    public getAvatarInBase64() {
        return new Promise((resolve) => {
            try {
                if (!this.avatar?.src) {
                    resolve(undefined);
                }
                if (this.avatar.src && !this.avatar.src.startsWith("data:image/png")) {
                    const img = new Image();
                    const canvas = document.createElement("canvas");
                    canvas.width = 120;
                    canvas.height = 120;
                    const ctx = canvas.getContext("2d");
                    ctx.rect(0, 0, 120, 120);
                    ctx.fillStyle = this.color;
                    ctx.fill();
                    img.onload = () => {
                        ctx.drawImage(img, 0, 0, 120, 120);
                        resolve(canvas.toDataURL("image/png"));
                    };
                    img.src = this.avatar.src;
                } else {
                    resolve(this.avatar.src);
                }
            } catch {
                resolve(undefined);
            }
        });
    }

    public dateEncode(date: string): string {
        return Array.from(date).reduce((buf: string, char: string) => { buf += char.charCodeAt(0).toString(16); return buf; }, '');
    }

    public updateAvatar(size: number = 256, forced: boolean = false) {
        const isDirectoryContact: boolean = (this.o365Flag || this.companyDirectoryFlag || this.outlookFlag || this.personalDirectoryFlag);

        // Directory contacts have no avatar managed by the below API
        // Check has been therefore added to avoid breaking the default avatar based on the contact's initials.
        if (this.dbId && this.lastAvatarUpdateDate && !isDirectoryContact) {
            this.avatarSrc = `${this.avatarServerURL}/api/avatar/${this.dbId}?size=${size}&update=${this.dateEncode(this.lastAvatarUpdateDate)}`;
            if (this.avatar) this.avatar = { src: this.avatarSrc };
        }

        this.createTextAvatarImage();

        // If this is a Rainbow TV, get the avatar from local sources
        if (this.isTv) {
            this.defaultAvatar = { src: "/assets/skins/rainbow/images/misc/androidtv.svg" };
        }

        // If the displayname refers to a phone number (unknown external number or number without name resolution),
        // we will not use the default avatar built from this number (so containing for example '+' or a digit)
        // but we override it with the 'unknownUser' avatar.
        let isDisplayNamePhoneNumber: boolean = false;
        isDisplayNamePhoneNumber = this.startAsPhoneNumber(this.displayName);
        if (isDisplayNamePhoneNumber || this.displayName === "Anonymous") {
            this.defaultAvatar = { src: "/assets/skins/rainbow/images/conversations/unknownUser.jpeg" };
        }

        if (forced || !this.lastAvatarUpdateDate) {
            this.avatar = null;
        }
    }

    public createTextAvatarImage() {
        const image = new Image();
        const canvas = document.createElement("canvas");
        canvas.width = image.width = 225;
        canvas.height = image.height = 225;

        const ctx = canvas.getContext("2d");
        ctx.rect(0, 0, 225, 225);
        ctx.fillStyle = this.color;
        ctx.fill();
        ctx.font = "bold 100px Helvetica";
        ctx.textAlign = "center";
        ctx.fillStyle = "white";
        ctx.fillText(this.initials, 110, 150);
        image.src = canvas.toDataURL("image/png");
        this.defaultAvatar = image;
    }

    /**
     * Set the 'avatar' with the current value of the default avatar
     * This is mainly useful for personal and business contacts for which no avatar can be defined at the moment.
     * Only the default avatar is available for them, based on the initials of lastname/firstname or company name
     * To take into account that the default avatar data can be changed later by the phone in a call situation,
     * (following an update of the display name), the default avatar also becomes the real avatar (until we can specify one)
     */
    public setAvatarFromDefault() {
        this.avatar = this.defaultAvatar;
        this.lastAvatarUpdateDate = new Date();
    }

    //  NAME STUFF
    // --------------------------------
    public updateName(firstName: string, lastName: string): void {
        this.firstname = firstName;
        this.lastname = lastName;
        this.computeDisplayName();
        this.updateAvatar();
    }

    public computeDisplayName() {
        const firstName = this.firstname ? this.firstname.charAt(0).toUpperCase() + this.firstname.slice(1) : null;
        const lastName = this.lastname ? this.lastname.charAt(0).toUpperCase() + this.lastname.slice(1) : null;
        const nickName = this.nickname ? this.nickname.charAt(0).toUpperCase() + this.nickname.slice(1) : null;

        // Check for Emoji into displayed name in order to remove them
        const emojiRegex = /\p{Emoji_Presentation}/gu;
        const specialCharRegex = /[\uFE0F]/gu;

        if (this.isTv) {
            this.displayName = lastName;
            this.initials = this.displayName.charAt(0);

            if (emojiRegex.test(lastName)) {
                this.initials = lastName.replaceAll(emojiRegex, '').replaceAll(specialCharRegex, '').trim().charAt(0);
            }
        } else if (!lastName && !firstName && (this.personalDirectoryFlag || this.companyDirectoryFlag)) {
            // Personal/Company directory contact defined with no firstname/lastname but only a company name (contact of type 'company')
            // The display name is deducded from the company name
            this.displayName = this.companyName;
            this.initials = this.displayName.charAt(0);
        } else if (lastName && firstName) {
            if (this.displayOrder === "FL") {
                this.displayName = firstName + " " + lastName;
                this.displayShortName = firstName + " " + lastName.charAt(0);
                this.initials = firstName.charAt(0) + lastName.charAt(0);

                if (emojiRegex.test(firstName) || emojiRegex.test(lastName)) {
                    this.initials = firstName.replaceAll(emojiRegex, '').replaceAll(specialCharRegex, '').trim().charAt(0) + lastName.replaceAll(emojiRegex, '').replaceAll(specialCharRegex, '').trim().charAt(0);
                }
            } else {
                this.displayName = lastName + " " + firstName;
                this.displayShortName = lastName + " " + firstName.charAt(0);
                this.initials = lastName.charAt(0) + firstName.charAt(0);

                if (emojiRegex.test(firstName) || emojiRegex.test(lastName)) {
                    this.initials = lastName.replaceAll(emojiRegex, '').replaceAll(specialCharRegex, '').trim().charAt(0) + firstName.replaceAll(emojiRegex, '').replaceAll(specialCharRegex, '').trim().charAt(0);
                }
            }
        } else if (lastName && !firstName) {
            this.displayName = this.displayShortName = lastName;
            this.initials = lastName.charAt(0);

            if (emojiRegex.test(lastName)) {
                this.initials = lastName.replaceAll(emojiRegex, '').replaceAll(specialCharRegex, '').trim().charAt(0);
            }
        } else if (nickName) {
            this.displayName = nickName;
            this.displayShortName = nickName;
            this.initials = nickName.charAt(0);

            if (emojiRegex.test(nickName)) {
                this.initials = nickName.replaceAll(emojiRegex, '').replaceAll(specialCharRegex, '').trim().charAt(0);
            }
        } else if (this.jid?.startsWith("mcu_")) {
            this.displayName = "MCUVideo BOT";
            this.displayShortName = "MCUVideo BOT";
            this.initials = "MB";
        } else if (this.loginEmail) {
            this.displayName = this.loginEmail;
            this.displayShortName = this.loginEmail;
            this.initials = this.loginEmail.substring(0, 2).toUpperCase();
        } else if (firstName) {
            this.displayName = firstName;
            this.initials = firstName.charAt(0);

            if (emojiRegex.test(firstName)) {
                this.initials = firstName.replaceAll(emojiRegex, '').replaceAll(specialCharRegex, '').trim().charAt(0);
            }
        } else {
            this.displayName = "Anonymous";
            this.displayShortName = "Anonymous";
            this.initials = "A";
        }

        // Compute contact color
        const upperCaseDisplayName = this.displayName.toUpperCase();
        let sum = 0;
        for (let i = 0; i < upperCaseDisplayName.length; i++) {
            sum += upperCaseDisplayName.charCodeAt(i);
        }
        this.colorIndex = sum % 12;
        this.color = Contact.textAvatarColor[this.colorIndex];
        this.colorName = Contact.textAvatarName[this.colorIndex];
    }

    //  GUEST USER STUFF
    // --------------------------------

    /**
     * Return true if user is a CPaaS guest, otherwise false.
     */
    public isCPaaSGuest(): boolean {
        return this.roles.indexOf(UserRole.GUEST) >= 0;
    }

    /**
     * Return true if user is an UCaaS guest, otherwise false.
     */
    public isGuest(): boolean {
        return this.guestMode;
    }

    public isAnonymousGuest(): boolean {
        return !this.isTv && (this.initials === "A" || this.displayName === "Anonymous");
    }

    public isBadGuest(): boolean {
        return this.displayName.startsWith("__guest__") || this.displayName.startsWith("__##__guest__##__");
    }

    public isGuestInitialized(): boolean {
        return (
            this.firstname !== null
            && this.firstname.length > 0
            && this.lastname !== null && this.lastname.length > 0
            && this.firstname !== "__##__guest__##__"
        );
    }

    //  ADMIN USER STUFF
    // --------------------------------

    /**
     * Return true if user has some administration rights, otherwise false.
     */
    public hasAdminRights(): boolean {
        return this.isSuperadmin() || this.isBusinessAdmin() || this.isAdmin() || this.isBPAdmin();
    }

    /**
     * Return true if user has superadmin rights, otherwise false.
     */
    public isSuperadmin(): boolean {
        return this.roles.indexOf(UserRole.SUPERADMIN) >= 0;
    }

    /**
     * Return true if user has business responsabilities within its company, otherwise false.
     */
    public isBusinessAdmin(): boolean {
        return this.roles.indexOf(UserRole.BUSINESS_ADMIN) >= 0;
    }

    /**
     * Return true if user has customer success responsabilities within its company, otherwise false.
     */
    public isCustomerSuccessAdmin(): boolean {
        return this.roles.indexOf(UserRole.CUSTOMER_SUCCESS_ADMIN) >= 0;
    }

    /**
     * Return true if user has role sales_analytics, otherwise false.
     */
    public isSalesAnalyticsAdmin(): boolean {
        return this.roles.indexOf(UserRole.SALES_ANALYTICS) >= 0;
    }

    /**
     * Return true if user has role sales_analytics, otherwise false.
     */
    public isPrivacyOperationAdmin(): boolean {
        return this.roles.indexOf(UserRole.PRIVACY_OPERATIONS) >= 0;
    }

    /**
     * Return true if user has the rights to manage all companies within its organization, otherwise false.
     */
    public isOrganizationAdmin(): boolean {
        return this.roles.indexOf(UserRole.ADMIN) >= 0 && this.adminType === AdminType.ORGANIZATION_ADMIN;
    }

    /**
     * Return true if user has the rights to manage support companies within its organization, otherwise false.
     */
    public isSupportAdmin(): boolean {
        return this.roles.indexOf(UserRole.ADMIN) >= 0 && this.adminType === AdminType.COMPANY_SUPPORT;
    }

    /**
     * Return true if user has the rights to manage its company, otherwise false.
     */
    public isCompanyAdmin(): boolean {
        return this.roles.indexOf(UserRole.ADMIN) >= 0 && this.adminType === AdminType.COMPANY_ADMIN;
    }

    /**
     * Return true if user has the rights to manage only the sites of its company, otherwise false.
     */
    public isSiteAdmin(): boolean {
        return this.roles.indexOf(UserRole.ADMIN) >= 0 && this.adminType === AdminType.SITE_ADMIN;
    }

    /**
     * Return true if user has the rights to manage its company, otherwise false.
     */
    public isAdmin(): boolean {
        return this.roles.indexOf(UserRole.ADMIN) >= 0;
    }

    /**
     * Return true if user is an administrator of a End Customer (EC) company, otherwise false.
     */
    public isECAdmin(): boolean {
        return !this.company?.isBP && this.isAdmin();
    }

    /**
     * Return true if user has operations or financial rights within a Business Partner (BP) company, otherwise false.
     */
    public isBPAdmin(): boolean {
        return this.isBPAdminOperations() || this.isBPAdminFinance();
    }

    /**
     * Return true if user has operations rights within a Business Partner (BP) company, otherwise false.
     */
    public isBPAdminOperations(): boolean {
        return this.company?.isBP && this.roles.indexOf(UserRole.BP_ADMIN_OPERATIONS) >= 0;
    }

    /**
     * Return true if user has finance rights within a Business Partner (BP) company, otherwise false.
     */
    public isBPAdminFinance(): boolean {
        return this.company?.isBP && this.roles.indexOf(UserRole.BP_ADMIN_FINANCE) >= 0;
    }

    /**
     * Return true if user has operations or financial rights within a Direct Reseller (DR) company, otherwise false.
     */
    public isBPAdminDR(): boolean {
        return this.isBPAdmin() && this.company?.isDR();
    }

    /**
     * Return true if user has operations or financial rights within a Value Added Distributor (VAD) company, otherwise false.
     */
    public isBPAdminVAD(): boolean {
        return this.isBPAdmin() && this.company?.isVAD();
    }

    /**
     * Return true if user has operations or financial rights within an Indirect Reseller (IR) company, otherwise false.
     */
    public isBPAdminIR(): boolean {
        return this.isBPAdmin() && this.company?.isIR();
    }

    /**
     * Return true if user has the rights to manage the business directory of its company, otherwise false.
     */
    public isDirectoryAdmin(): boolean {
        return this.roles.indexOf(UserRole.DIRECTORY_ADMIN) >= 0;
    }

    /**
     * Return true if user has customer analytics rights within a Business Partner (BP) company, otherwise false.
     */
    public isBPAnalytics(): boolean {
        return this.company?.isBP && this.roles.indexOf(UserRole.BP_ADMIN_ANALYTICS) >= 0;
    }

    /**
     * Return true if user has analytics rights within a Business Partner (BP) or End Customer (EC) company, otherwise false.
     */
    public isECAnalytics(): boolean {
        return this.roles.indexOf(UserRole.EC_ADMIN_ANALYTICS) >= 0;
    }

    /**
     * Return true if user has the rights to manage webinars of its company, otherwise false.
     */
    public isWebinarHost(): boolean {
        return this.roles.indexOf(UserRole.WEBINAR_HOST) >= 0;
    }

    /**
     * Return true if user has the role of supervisor : this means that he's got a supervvisor role in at least one supervision group
     */
    public isSupervisor(): boolean {
        return (this.roles && this.roles.indexOf(UserRole.GROUP_SUPERVISOR) >= 0);
    }

    /**
     * Return the admin type
     */
    public getAdminType(): string {
        if (this.roles.indexOf(UserRole.ADMIN) >= 0) {
            return this.adminType;
        }
        return AdminType.UNDEFINED;
    }

    /**
     * Indicates wether the user belongs to a Business Partner (BP) company or not.
     */
    public isBPUser(): boolean {
        return this.company?.isBP;
    }

    //  TELEPHONY STUFF
    // --------------------------------

    /**
     * Indicates wether the connected user is an hybrid user and is on an hybrid system with the capability
     * 'DialOut' in order to call a pstn number and add it in web conference
     */
    public isHybridDialOutCapability(): boolean {
        return this.isRainbowHybridUser() && this.dialOutCapability;
    }

    /**
     * Indicates wether the connected user is an hybrid oxe user and is on an oxe system with the capability
     * 'oxePBXOverflowCapability' in order to redirect incoming voip calls when user is unreachable
     */
    public isOxeOverflowCapability(): boolean {
        return this.isRainbowHybridUser() && this.oxePBXOverflowCapability;
    }

    /**
     * Indicates wether the connected user is an hybrid user and is on an hybrid system with the capability
     *  'Join by phone' in order to join by phone a web conference
     */
    public isHybridJoinByPhoneCapability(): boolean {
        return this.isRainbowHybridUser() && this.joinByPhoneCapability;
    }

    //  SUPERVISION Stuff
    // ----------------------------------
    public getSupervisionDevices() {
        return this._supervisionDevices ? Object.keys(this._supervisionDevices) : [];
    }

    public getSupervisionDeviceState(device: string) {
        return this._supervisionDevices ? this._supervisionDevices[device] : null;
    }

    public setSupervisionDeviceState(device: string, state: string) {
        if (!this._supervisionDevices) { this._supervisionDevices = {}; }
        if (state !== "free") {
            this._supervisionDevices[device] = state;
        } else {
            delete this._supervisionDevices[device];
        }

        // compute  user's supervision state from devices supervision states
        const existWaitingDevice = Object.keys(this._supervisionDevices).some((deviceNumber: string) => {
            const deviceState = this._supervisionDevices[deviceNumber];
            return deviceState === "waiting";
        });
        if (existWaitingDevice) {
            this.supervisionState = "waiting";
            return;
        }

        const existRingingInDevice = Object.keys(this._supervisionDevices).some((deviceNumber: string) => {
            const deviceState = this._supervisionDevices[deviceNumber];
            return deviceState === "ringingIn";
        });
        if (existRingingInDevice) {
            this.supervisionState = "ringingIn";
            return;
        }

        const existEngagedOutDevice = Object.keys(this._supervisionDevices).some((deviceNumber: string) => {
            const deviceState = this._supervisionDevices[deviceNumber];
            return deviceState === "engagedOut";
        });
        if (existEngagedOutDevice) {
            this.supervisionState = "engagedOut";
            return;
        }

        const existBusyDevice = Object.keys(this._supervisionDevices).some((deviceNumber: string) => {
            const deviceState = this._supervisionDevices[deviceNumber];
            return deviceState === "busy";
        });
        if (existBusyDevice) {
            this.supervisionState = "busy";
            return;
        }

        this.supervisionState = "free";
    }

    public resetSupervisionState() {
        this.supervisionState = "free";
        this._supervisionDevices = {};
    }

    public resetSupervisionCallinfos() {
        this.supervisionCallInfos = [];
    }

    public updateSupervisionCallInfo(supervisionCallInfo: any): void {
        const callInfoToUpdateIndex: number = this._supervisionCallInfos.findIndex(callInfo => callInfo.callId === supervisionCallInfo.callId);
        if (callInfoToUpdateIndex !== -1) {
            //replace old callinfo by new one, but keep value of 'notified' property of previous call
            supervisionCallInfo['notified'] = this._supervisionCallInfos[callInfoToUpdateIndex].notified;
            this._supervisionCallInfos.splice(callInfoToUpdateIndex, 1, supervisionCallInfo);
            this.rxSubject.next({ name: "supervisionCallInfos" });
        }
    }

    //  UTILITIES STUFF
    // --------------------------------

    /**
     * Returns a string representing the complete Contact object.
     */
    public toString(): string {
        return this.nameForLogs;
    }

    public containsEmail(email: string): boolean {
        const lowerCaseEmail = email.toLowerCase();
        if (this.loginEmail && this.loginEmail.toLowerCase() === lowerCaseEmail) {
            return true;
        }
        if (this.emailPro && this.emailPro.toLowerCase() === lowerCaseEmail) {
            return true;
        }
        if (this.emailPerso && this.emailPerso.toLowerCase() === lowerCaseEmail) {
            return true;
        }
        return false;
    }

    public containsPhoneNumber(phoneNumber: string) {
        if (!phoneNumber) {
            return false;
        }
        return [
            this.phonePbx,
            this.phoneInternalNumber,
            this.phonePro,
            this.phoneProCan,
            this.mobilePro,
            this.mobileProCan,
            this.phonePerso,
            this.phonePersoCan,
            this.mobilePerso,
            this.mobilePersoCan
        ].includes(phoneNumber);
    }

    public containsDirectoryPhoneNumber(phoneNumber: string) {
        if (!phoneNumber) {
            return false;
        }

        const mobilePhones: string[] = this.mobilePhones || [];
        const businessPhones: string[] = this.businessPhones || [];
        const otherPhoneNumbers: string[] = this.otherPhoneNumbers || [];

        if (mobilePhones.includes(phoneNumber))
            return true;

        if (businessPhones.includes(phoneNumber))
            return true;

        if (otherPhoneNumbers.includes(phoneNumber))
            return true;

        return false;
    }

    public matchesSearchCriteria(criteria: string): boolean {
        return this.contactService.contactMatcher(this, criteria);
    }

    /**
     * Check if the given string corresponds to a phone number (canonical format or not).
     * @param phoneNumber - The string to check
     */
    public startAsPhoneNumber(phoneNumber: string): boolean {
        const cleanPhoneNumber = phoneNumber.trim().split(".").join("");
        if (cleanPhoneNumber.length < 1) { return false; }
        const pattern1 = /^(\+|\d|#|\*|\(|\)|\.|-|–|—|\s|\/)*$/;
        const match = cleanPhoneNumber.match(pattern1);
        if (!match) { return false; }
        return (match[0] === cleanPhoneNumber);
    }

    // ----------------------------------------------------
    // CONTACT INSTANCE DEBUG (USED BY SDKv2)
    // ----------------------------------------------------

    public toJSON(): any {
        const json: any = {};
        json.id = this.dbId;
        json.jid = this.jid;
        json.presenceStatus = this.status + this.statusInfo ? this.statusInfo : '';
        if (this.title) json.title = this.title;
        if (this.displayName) json.displayName = this.displayName;
        if (this.firstname) json.firstname = this.firstname;
        if (this.lastname) json.last = this.lastname;
        if (this.nickname) json.nickname = this.nickname;
        json.userType = this.userType;

        if (this.lastAvatarUpdateDate) json.lastAvatarUpdateDate = this.lastAvatarUpdateDate;
        if (this.jobTitle) json.jobTitle = this.jobTitle;
        if (this.country) json.country = this.country;
        if (this.language) json.language = this.language;
        if (this.timezone) json.timezone = this.timezone;

        json.emailAddresses = [];
        this.emails?.forEach((email: UserEmailAddress) => {
            json.emailAddresses.push({ email: email.address, type: email.type });
        });

        json.phoneNumbers = [];
        this.phoneNumbers?.forEach((phoneNr: UserPhoneNumber) => {
            const phoneNumberData: any = {};
            if (phoneNr.hasOwnProperty('number')) phoneNumberData.number = phoneNr.number;
            if (phoneNr.hasOwnProperty('type')) phoneNumberData.type = phoneNr.type;
            if (phoneNr.hasOwnProperty('shared')) phoneNumberData.shared = phoneNr.shared;
            if (phoneNr.hasOwnProperty('readOnly')) phoneNumberData.readOnly = phoneNr.readOnly;
            json.phoneNumbers.push(phoneNumberData);
        });

        if (this.displayOrder) json.displayOrder = this.displayOrder;

        // Users + Company Business contacts: display Company info if available
        if (this.company) {
            if (this.company.id) json.companyId = this.company.id;
        }
        else if (this.companyDirectoryFlag) {
            if (this.businessCompanyId) json.businessCompanyId = this.businessCompanyId;
        }
        return json;
    }

}
